import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Card, Table, Tag, Typography, Space, DatePicker, Empty, StatCard, theme, Tooltip, Segmented, Spin, Modal, Input, Select, Alert, cn,
} from '../../ui';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import {
  Route as RouteIcon, Download, ShoppingCart, IndianRupee, Wallet, MapPin, CalendarCheck,
  Search, UserPlus, TriangleAlert, X as XIcon, Eye, FileText,
} from 'lucide-react';
import { Button, FilterResetButton, message } from '../../ui';
import { useThemeStore } from '../../store/theme';
import { downloadCsv } from '../../lib/downloadCsv';
import { previewPdf } from '../../lib/pdf';
import { disableFuture } from '../../lib/dateRange';
import { api, request } from '../../api/client';
import { formatIst, formatIstDate, formatIstTime } from '../../lib/time';
import { usePermission } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useUrlStringOrNull } from '../../hooks/useUrlState';
import { useServerSort } from '../../hooks/useServerSort';
import { RoleBadge } from '../../components/RoleBadge';
import { useAuthStore } from '../../store/auth';
import { RouteMapModal } from '../../components/RouteMapModal';
import { PALETTE, TAG_TONE_HUE, filterTint } from '../../lib/palette';
import { inr as inrWhole } from '../../lib/format';

// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;
const num = (v: any) => Number(v ?? 0).toLocaleString('en-IN');
const km = (v: any) => `${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })} km`;

/**
 * Two counts shown as the PAIR they are — "1 / 2" — never as the decimal they divide to.
 *
 * "Orders / visit" used to render 1.0 and 0.0, which reads as a score out of one (and drew the
 * question "why is this a decimal?"). The pair states the two numbers it is made of, so it explains
 * itself, cannot be mistaken for a percentage, and stays honest when the top exceeds the bottom.
 */
const pairOf = (a: any, b: any) => `${num(a)} / ${num(b)}`;

/** "x of y" as a percentage, but never printing 0% for a non-zero share — ₹319 realized against
 *  ₹4.97L is 0.06%, and "0%" is the one thing it definitely is not. */
const shareOf = (part: any, whole: any): string => {
  const p = Number(part ?? 0), w = Number(whole ?? 0);
  if (!(w > 0)) return '—';
  if (p > 0 && p / w < 0.005) return '<1%';
  return `${Math.round((p / w) * 100)}%`;
};

/** The "everything at this level" choice. A real option rather than an empty select, so the control
 *  reads as answered ("All states") instead of unset. */
const ALL_T = '*';

export type TerritoryPick = { state: string | null; district: string | null; beat: string | null };
export const NO_TERRITORY: TerritoryPick = { state: null, district: null, beat: null };

/** The deepest node chosen is the filter — the server expands it down the tree from there. */
export const territoryIdOf = (t: TerritoryPick) => t.beat ?? t.district ?? t.state ?? null;

/**
 * State → District → Beat, cascading: each level offers only the children of the one above, and a
 * level is not shown at all until its parent is a specific place. Choosing "All districts" means the
 * question is already answered at district level, so the beat control would only be noise.
 *
 * Options come from the SERVER's facets (territories that actually have staff in scope), so the
 * cascade never offers a beat with nobody on it. The count is staff at or under that node.
 */
function useTerritoryCascade(facets: any[] = [], pick: TerritoryPick, set: (t: TerritoryPick) => void) {
  // Every territory at this level is listed, so the geography is visible — but one with nobody in it
  // is DISABLED rather than hidden: you can see where you have no staff without being able to filter
  // yourself into an empty table.
  const at = (level: string, parentId: string | null) => facets
    .filter((t: any) => t.level === level && (parentId === null || String(t.parentId) === String(parentId)))
    .map((t: any) => ({ label: `${t.name} (${t.staff})`, value: String(t.id), disabled: !Number(t.staff) }))
    .sort((a: any, b: any) => Number(a.disabled) - Number(b.disabled) || a.label.localeCompare(b.label));

  const states = at('state', null);
  const districts = pick.state ? at('district', pick.state) : [];
  const beats = pick.district ? at('beat', pick.district) : [];
  return {
    states, districts, beats,
    // Changing a level always clears the levels beneath it — a district from the previous state
    // would otherwise stay applied and quietly contradict the state now shown.
    onState: (v: string) => set({ state: v === ALL_T ? null : v, district: null, beat: null }),
    onDistrict: (v: string) => set({ ...pick, district: v === ALL_T ? null : v, beat: null }),
    onBeat: (v: string) => set({ ...pick, beat: v === ALL_T ? null : v }),
  };
}

/** The three selects, rendered only as deep as the pick goes. */
export function TerritoryCascade({ facets, pick, set, width = 170 }: {
  facets: any[]; pick: TerritoryPick; set: (t: TerritoryPick) => void; width?: number;
}) {
  const c = useTerritoryCascade(facets, pick, set);
  if (!c.states.length) return null;
  return (
    <>
      <Select
        size="sm" showSearch optionFilterProp="label" placeholder="All states"
        value={pick.state ?? ALL_T} onChange={(v: any) => c.onState(String(v ?? ALL_T))}
        options={[{ label: 'All states', value: ALL_T }, ...c.states]}
        style={{ width }}
      />
      {/* Only once a specific state is chosen — "All states" has already answered the question. */}
      {pick.state && c.districts.length > 0 && (
        <Select
          size="sm" showSearch optionFilterProp="label" placeholder="All districts"
          value={pick.district ?? ALL_T} onChange={(v: any) => c.onDistrict(String(v ?? ALL_T))}
          options={[{ label: 'All districts', value: ALL_T }, ...c.districts]}
          style={{ width }}
        />
      )}
      {pick.district && c.beats.length > 0 && (
        <Select
          size="sm" showSearch optionFilterProp="label" placeholder="All beats"
          value={pick.beat ?? ALL_T} onChange={(v: any) => c.onBeat(String(v ?? ALL_T))}
          options={[{ label: 'All beats', value: ALL_T }, ...c.beats]}
          style={{ width }}
        />
      )}
    </>
  );
}

/** What each KPI-band tile filters to — used for the applied-filter chip. */
const ACTIVITY_LABEL: Record<string, string> = {
  active: 'Did work',
  orders: 'Placed an order',
  orderValue: 'Booked order value',
  revenue: 'Realized revenue',
  visits: 'Completed a visit',
  notReached: 'Set off but never reached',
  newCustomers: 'Added a customer',
  expenses: 'Claimed expenses',
  flagged: 'Has a flagged day',
  noWorkClaimed: 'Claimed against a day with no work',
  absent: 'Was absent',
  pending: 'Has customers whose visit rule is not met',
};

/**
 * The measured-value filters: each one a band of a column that is already on the table, so the
 * toolbar asks the questions the columns answer. The boundaries are the ones those columns are
 * already coloured by (green at 90, gold at 70, and so on), or a band picked from the toolbar would
 * select a different set of rows than the colour a reader was looking at implies.
 *
 * Every one of them is resolved on the SERVER — `BOARD_ATTENDANCE`, `BOARD_COVERAGE` and `boardFlag`
 * in the performance module — for the same reason the scope filters are: the KPI band has to keep
 * counting the whole scope while the table shows a subset, which only the server can do honestly.
 */
type Band = { value: string; label: string; tone: 'green' | 'gold' | 'red' | 'neutral' };

const COVERAGE_BANDS: Band[] = [
  { value: 'most', label: 'Coverage 80% or more', tone: 'green' },
  { value: 'some', label: 'Coverage 40–79%', tone: 'gold' },
  { value: 'few', label: 'Coverage under 40%', tone: 'red' },
  /* A band of its own, not the bottom of the scale: a rep none of whose customers has a visit rule this
     window has not covered 0% — nothing is owed — which is exactly why that column prints an em dash. */
  { value: 'nobook', label: 'No visit rule applies', tone: 'neutral' },
];
const ATTENDANCE_BANDS: Band[] = [
  { value: 'full', label: 'Attendance 90% or more', tone: 'green' },
  { value: 'partial', label: 'Attendance 70–89%', tone: 'gold' },
  { value: 'short', label: 'Attendance under 70%', tone: 'red' },
];
const bandLabel = (bands: Band[], v: string) => bands.find((b) => b.value === v)?.label ?? v;

/** Specific flags in the order a reviewer should read them — the ones that cost money first. */
const FLAG_FILTER_ORDER = [
  'PAID_ON_NO_WORK', 'NO_WORK_CLAIMED', 'DISTANCE_MISMATCH', 'IMPOSSIBLE_TRAVEL',
  'NO_WORK', 'SHORT_DAY', 'FACE_MISMATCH', 'OUTSIDE_GEOFENCE', 'AUTO_CHECKOUT',
];

/**
 * One measured-value filter, with two rules the plain selects beside it do not carry.
 *
 * Each option wears the DOT its own value wears in the table, so the control and the rows agree
 * about what green means. And each carries the number of staff clicking it would LEAVE — the
 * INVERSE of the KPI-tile rule, which counts the whole scope — so an option that would empty the
 * table says so before it is picked, and is disabled rather than clickable.
 */
function FacetSelect({ placeholder, value, onChange, options, counts, width = 185 }: {
  placeholder: string;
  value: string | null;
  onChange: (v: string | null) => void;
  options: Band[];
  /** How many staff each option would leave — the server's own facet counts. */
  counts: Record<string, number>;
  width?: number;
}) {
  const tone = value ? options.find((o) => o.value === value)?.tone : null;
  return (
    <Select
      size="sm"
      allowClear
      placeholder={placeholder}
      aria-label={placeholder}
      value={value}
      onChange={(v: any) => onChange(v ?? null)}
      optionFilterProp="searchLabel"
      optionLabelProp="selectedText"
      /* A set filter wears the colour of what it selected, so a row of identical grey controls still
         says which one is narrowing the list. */
      style={{ width, ...(tone ? filterTint(TAG_TONE_HUE[tone]) : {}) }}
      options={options.map((o) => {
        const n = Number(counts?.[o.value] ?? 0);
        return {
          value: o.value,
          disabled: !n,
          searchLabel: o.label,
          selectedText: o.label,
          label: (
            <span className="flex w-full items-center gap-2">
              <span className="inline-block size-2 shrink-0 rounded-full" style={{ background: TAG_TONE_HUE[o.tone] }} />
              <span className="truncate">{o.label}</span>
              <span className="ml-auto pl-2 text-2xs tabular-nums text-subtle">{num(n)}</span>
            </span>
          ),
        };
      })}
    />
  );
}

/** Work-integrity flags, in the order a reviewer should read them. `high` is the case that costs
 *  money (claimed / already paid against a day with nothing to show); `low` is an evidence-quality
 *  note — the GPS producer isn't live yet, so those are shown but never counted as anomalies. */
const FLAG_META: Record<string, { label: string; tone: 'red' | 'gold' | 'neutral'; hint: string }> = {
  PAID_ON_NO_WORK: { label: 'Paid, no work', tone: 'red', hint: 'Expenses were settled for a day with no visit, order or new customer.' },
  NO_WORK_CLAIMED: { label: 'Claimed, no work', tone: 'red', hint: 'Expenses were claimed for a day with no visit, order or new customer.' },
  DISTANCE_MISMATCH: { label: 'Km mismatch', tone: 'red', hint: 'Declared distance is well above what the GPS trace supports.' },
  IMPOSSIBLE_TRAVEL: { label: 'Impossible travel', tone: 'red', hint: 'Two stops that day are too far apart for the time between them — check the locations.' },
  NO_WORK: { label: 'No work', tone: 'gold', hint: 'On duty, but no completed visit, order or new customer recorded.' },
  SHORT_DAY: { label: 'Short day', tone: 'gold', hint: 'Less than 4 hours between check-in and check-out.' },
  FACE_MISMATCH: { label: 'Face mismatch', tone: 'gold', hint: 'Punch-in face score was below threshold.' },
  OUTSIDE_GEOFENCE: { label: 'Outside geofence', tone: 'gold', hint: 'Punched in away from the expected location.' },
  AUTO_CHECKOUT: { label: 'Auto check-out', tone: 'gold', hint: 'The shift was closed by the system, not by the rep.' },
  DISTANCE_UNVERIFIED: { label: 'Km unverified', tone: 'neutral', hint: 'Distance was declared but no GPS trace exists to check it against.' },
  NO_GPS: { label: 'No GPS', tone: 'neutral', hint: 'The app sent no location for this day.' },
  OFFLINE_PUNCH: { label: 'Punched offline', tone: 'neutral', hint: 'The phone had no signal, so it recorded the time itself and sent the punch later.' },
};
const flagMeta = (f: string) => FLAG_META[f] ?? { label: f.replace(/_/g, ' ').toLowerCase(), tone: 'gold' as const, hint: '' };
const isCounted = (f: string) => flagMeta(f).tone !== 'neutral';

/**
 * What a drilled tile actually SELECTED ON, and where the proof of it lives.
 *
 * Drilling "1 claimed, no work" landed the reader on one staff row that said nothing about why it
 * was the answer: the number came from a DAY, and a row is a period. Orders 17, visits 4, present 5 —
 * every visible column argued the opposite of the filter that had just selected her. The evidence
 * existed (the Flags column, and the flagged day inside the drill-down) but sat past 1,700px of
 * horizontal scroll and two more clicks, with nothing tying any of it to the figure clicked.
 *
 * So a lane declares three things: what it MEANS in the reader's words, the row's own count for it
 * with the unit that count is in, and the day-grid filter that isolates the proof. Only lanes whose
 * basis is NOT already a column on the row need an entry — "Placed an order" needs no explaining
 * next to an Orders column reading 17.
 */
const LANE_BASIS: Record<string, {
  /** One sentence: what a row had to do to be here. */
  words: string;
  /** This row's own figure for the lane, ALREADY worded — each lane says its own count, because
   *  "1 visit set off for, never reached" does not pluralise by adding an s to the end. */
  evidence?: (r: any) => string | null;
  /** Pre-set on the rep's day grid when their row is opened, so the proof is the first thing shown. */
  day?: DayFilter;
}> = {
  active: {
    words: 'Recorded at least one order, visit or new customer in this period. A rep with none of the three did no work the board can see.',
  },
  notReached: {
    words: 'Set off for at least one visit and never arrived. These carry no check-in, so they are NOT in the Visits done column — which is why that column can read 0 on a row selected here.',
    evidence: (r) => (r.visitsCancelled > 0
      ? `${r.visitsCancelled} ${r.visitsCancelled === 1 ? 'visit' : 'visits'} set off for, never reached` : null),
  },
  noWorkClaimed: {
    // The definition is the flag's own — one wording for the flag, the tile and this line.
    words: FLAG_META.NO_WORK_CLAIMED.hint,
    evidence: (r) => (r.noWorkClaimedDays > 0
      ? `${r.noWorkClaimedDays} ${r.noWorkClaimedDays === 1 ? 'day' : 'days'} claimed with no work` : null),
    day: { flag: 'NO_WORK_CLAIMED' },
  },
  flagged: {
    words: 'Carries at least one work-integrity flag on at least one day of this period.',
    evidence: (r) => (r.anomalyDays > 0
      ? `${r.anomalyDays} flagged ${r.anomalyDays === 1 ? 'day' : 'days'}` : null),
    day: { flag: 'ANY' },
  },
};

type Preset = 'today' | 'week' | 'month' | 'year' | 'custom';
function presetRange(p: Preset, custom: [Dayjs, Dayjs] | null): [string, string] {
  const today = dayjs();
  switch (p) {
    case 'today': return [today.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'week': return [today.startOf('week').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'year': return [today.startOf('year').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'custom': return custom && custom[0] && custom[1] ? [custom[0].format('YYYY-MM-DD'), custom[1].format('YYYY-MM-DD')] : [today.startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    case 'month':
    default: return [today.startOf('month').format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
  }
}

export function SalesPerformancePage() {
  const { token } = theme.useToken();
  const [preset, setPreset] = useState<Preset>('month');
  const [custom, setCustom] = useState<[Dayjs, Dayjs] | null>(null);
  /* Which rep's day is open, IN THE URL. A route-deviation alert names the rep it is about, and a
     manager who taps it wants that rep's days and map — not a table of everyone to find them in
     again. `?rep=` is also what makes Back close the drill instead of leaving the page. */
  const [drillId, setDrillId] = useUrlStringOrNull('rep');
  const [exportOpen, setExportOpen] = useState(false);
  // ── Filters, every one resolved on the SERVER. Only the server can do both halves of this job at
  //    once: narrow the table to a subset while still counting the KPI band over the whole scope.
  //    Filtering in the browser would either collapse the tiles toward zero or leave them disagreeing
  //    with the rows underneath them.
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const [territory, setTerritory] = useState<TerritoryPick>(NO_TERRITORY);
  const territoryId = territoryIdOf(territory);
  const [activity, setActivity] = useState<string | null>(null);
  // The measured-value filters — bands of the Coverage, Attend. % and Flags columns.
  const [coverage, setCoverage] = useState<string | null>(null);
  const [attendance, setAttendance] = useState<string | null>(null);
  const [flag, setFlag] = useState<string | null>(null);
  const q = useDebounce(search.trim(), 350);
  /* The ORDER is the server's too. It has to be: the export is this screen in a file, so the file
     has to be able to come out in the order the reader put the table in, and a browser-side sort is
     something the server cannot be told about. Kept in the URL, so Back restores it. */
  const { sortState, onSortChange, sort } = useServerSort({ defaultSort: { key: 'orders', dir: 'desc' } });
  // Claim amounts are money — only shown to someone who is allowed to see expenses at all.
  const canSeeMoney = usePermission('EXPENSE.VIEW');

  const [from, to] = useMemo(() => presetRange(preset, custom), [preset, custom]);

  /**
   * The query the board is showing — built ONCE and used for the fetch AND for the export, so the
   * file cannot be taken through a different filter set than the screen it was taken from. This is
   * the whole reason the export dialog has no filters of its own.
   */
  const boardParams = useMemo(() => ({
    from, to,
    q: q || undefined, role: role || undefined, territoryId: territoryId || undefined,
    activity: activity || undefined, coverage: coverage || undefined,
    attendance: attendance || undefined, flag: flag || undefined,
    sort: sort || undefined,
  }), [from, to, q, role, territoryId, activity, coverage, attendance, flag, sort]);

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['performance', 'sales', boardParams],
    queryFn: () => request<any>({ url: '/performance/sales', params: boardParams }),
    // Keep the last board on screen while a filter re-fetches — a table that blanks on every
    // keystroke reads as broken.
    placeholderData: keepPreviousData,
  });
  /**
   * A board that FAILED to load is not a board that matched nobody, and the screen must not say it
   * was. With `data` undefined every derived figure below collapses to zero, which rendered the
   * request failure as a perfectly ordinary empty result: "0 of 0 field staff", "No field staff
   * match these filters", no KPI band and no territory filter — a reader who had just clicked a tile
   * reading "1 claimed, no work" was told, wrongly, that their filter excluded everyone.
   *
   * `loadFailed` is what separates the two. Every place that would otherwise print a zero or the
   * word "none" checks it first and says "could not load" instead.
   */
  const loadFailed = isError && !data;
  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const staffTotal = Number(data?.staffTotal ?? 0);
  const staffCounts: Record<string, number> = totals?.staff ?? {};
  const facets = data?.facets ?? {};
  const anyFilter = !!(q || role || territoryId || activity || coverage || attendance || flag);
  const clearFilters = () => {
    setSearch(''); setRole(null); setTerritory(NO_TERRITORY); setActivity(null);
    setCoverage(null); setAttendance(null); setFlag(null);
  };

  /* Only the flags somebody in scope actually carries. A dropdown of eleven flag types where nine
     can never match is a list to read past, not a filter; the two spanning lenses lead it. */
  const flagOptions: Band[] = useMemo(() => {
    const counts: Record<string, number> = facets.flags ?? {};
    return [
      // Not "Any flag" — that is this control's PLACEHOLDER, and a chosen option reading exactly
      // what the unset control read is a filter you cannot tell you have applied.
      { value: 'ANY', label: 'Carries any flag', tone: 'gold' },
      { value: 'HIGH', label: 'Carries a high-severity flag', tone: 'red' },
      ...FLAG_FILTER_ORDER.filter((k) => k in counts).map((k) => ({ value: k, label: flagMeta(k).label, tone: flagMeta(k).tone })),
    ];
  }, [facets.flags]);

  /** The filters in words, printed back in the export dialog so a file states its own scope. */
  const territoryName = territoryId
    ? (facets.territories ?? []).find((x: any) => String(x.id) === String(territoryId))?.name ?? null
    : null;
  const scopeWords = useMemo(() => {
    const out: string[] = [];
    if (q) out.push(`Name contains “${q}”`);
    if (role) out.push(role === 'SALES_HEAD' ? 'Sales Heads only' : 'Sales Persons only');
    if (territoryName) out.push(`${territoryName} and everything under it`);
    if (activity) out.push(ACTIVITY_LABEL[activity] ?? activity);
    if (coverage) out.push(bandLabel(COVERAGE_BANDS, coverage));
    if (attendance) out.push(bandLabel(ATTENDANCE_BANDS, attendance));
    if (flag) {
      out.push(flag === 'ANY' ? 'Carries a work-integrity flag'
        : flag === 'HIGH' ? 'Carries a high-severity flag'
        : `Flagged: ${flagMeta(flag).label}`);
    }
    return out;
  }, [q, role, territoryName, activity, coverage, attendance, flag]);

  /**
   * Compact KPI — and every tile is a FILTER: clicking one narrows the table to the staff that tile
   * is counting, clicking it again clears. The whole set sits on ONE row, so the card stays narrow:
   * label on a single truncated line, the figure sized for a lakh-scale rupee amount, and a third
   * line naming what the figure is made of.
   *
   * The numbers are server-counted over the whole scope and deliberately IGNORE the tile filter, so
   * selecting one tile never drags its siblings toward zero (the house KPI rule).
   *
   * The tint is derived from the tile's own accent with an alpha suffix instead of a hardcoded light
   * hex, so the band survives dark mode rather than painting eight pastel slabs on a dark page.
   */
  /**
   * A figure ON a tile's sub-line that names a population of its own, made clickable.
   *
   * The band used to carry three such figures with no way back to their rows — "5 did work",
   * "1 not reached", "1 claimed, no work". Clicking the TILE could never answer them: it drills to
   * the tile's headline population, which for "not reached" is the one set of staff that
   * by definition cannot contain them (a visit never reached has no check-in, so it is not a visit).
   * Each now drills to its own lane, and the tile keeps its own.
   */
  const SubLink = ({ text, filter, count, color }: {
    text: string; filter: string; color: string;
    /** Staff the lane would keep. 0 → printed as plain text: a link that can only empty the table
     *  is the thing this whole change exists to remove. */
    count?: number;
  }) => {
    if (!count) return <>{text}</>;
    const on = activity === filter;
    return (
      <button
        type="button"
        aria-pressed={on}
        aria-label={`${text} — show only these ${count} staff`}
        onClick={() => setActivity(on ? null : filter)}
        className={cn(
          // Opts back into the pointer: its ancestor turns events OFF so the rest of the tile stays
          // one big click target for the tile's own filter.
          'pointer-events-auto rounded-xs underline decoration-dotted underline-offset-2',
          'hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40',
        )}
        style={on ? { color, fontWeight: 600 } : undefined}
      >
        {text}
      </button>
    );
  };

  const Kpi = ({ label, value, sub, color, filter, hint, count }: {
    label: string; value: string; sub?: ReactNode; color: string; filter?: string; hint: string;
    /** How many staff this tile's filter would keep. 0 → the tile is not a filter (see below). */
    count?: number;
  }) => {
    // The lead tile owns no filter: it reads as selected when nothing is applied, and clears the rest.
    const isReset = filter === undefined;
    // A tile that counts NOBODY must not be clickable. Clicking it could only ever empty the table,
    // which is exactly what made "why is this empty?" the first question this band was asked.
    const dead = !isReset && count === 0;
    const on = isReset ? !anyFilter : activity === filter;
    return (
      <Tooltip title={dead ? `${hint.replace(/ Click to show.*$/, '')} Nothing to filter to — no staff in this period.` : hint} block>
        {/* A DIV, not a button, because the sub-line now carries its own drills and a button nested
            inside a button is invalid markup that no keyboard can reach. The tile's own click target
            is the inset overlay below — the same hit area as before, but a SIBLING of the sub-line's
            buttons rather than their ancestor. */}
        <div
          className={cn(
            'relative h-full w-full overflow-hidden rounded-card border px-3 py-2 text-left transition duration-150',
            dead ? 'cursor-default' : 'hover:-translate-y-0.5 hover:shadow-card',
            on ? 'shadow-card' : 'border-line',
          )}
          style={{
            borderColor: on ? color : undefined,
            borderLeftColor: color,
            borderLeftWidth: on ? 5 : 3,
            background: on ? `${color}24` : `${color}12`,
          }}
        >
          <button
            type="button"
            aria-pressed={dead ? undefined : on}
            aria-disabled={dead || undefined}
            aria-label={isReset ? `${label} ${value} — show every field staff member` : `${label} ${value} — ${ACTIVITY_LABEL[filter!] ?? filter}`}
            onClick={dead ? undefined : () => (isReset ? clearFilters() : setActivity(on ? null : filter))}
            // The ring is INSET because the tile clips its overflow: an outset ring on an absolutely
            // positioned child is cut away, leaving the tile looking unfocusable.
            className="absolute inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40"
          />
          {/* Transparent to the pointer so the whole tile stays one click target for its own filter;
              the sub-line's links opt back in one by one. */}
          <div className="pointer-events-none relative">
            <div className="truncate text-xs" style={{ color: token.colorTextSecondary, lineHeight: 1.4 }}>{label}</div>
            <div className="truncate type-metric-sm" style={{ color, lineHeight: 1.35 }}>{value}</div>
            {/* Always rendered (nbsp when empty) so tiles with and without a sub-line keep one height.
                It WRAPS rather than truncating: this line now carries drill links, and `truncate`
                (nowrap + overflow-hidden) pushed the last one past the tile's clipped edge at
                narrower widths — where it stayed nominally visible to the DOM but un-clickable,
                because the tile's own click overlay was what sat under the pointer. Grid items
                stretch, so a wrapped line grows the whole row evenly rather than one tile. */}
            <div className="text-2xs leading-tight" style={{ color: token.colorTextTertiary }}>{sub || '\u00a0'}</div>
          </div>
        </div>
      </Tooltip>
    );
  };

  /*
   * `sorter: true` on every sortable column, never a comparator: the ORDER is the SERVER's (see
   * BOARD_SORT in the performance module), so the export can be handed the very `sort` the header is
   * showing and come out in the same order. A browser-side comparator cannot be put in a file.
   */
  const columns: any[] = [
    { title: 'Staff', dataIndex: 'name', sorter: true, fixed: 'left', width: 200,
      render: (v: string, r: any) => {
        // Why THIS row is in THIS result, on the one column that is frozen and therefore always on
        // screen. The Flags column carries the same fact but sits past ~1,700px of horizontal
        // scroll, so on a drilled board it answers a question the reader has already given up on.
        const why = activity ? LANE_BASIS[activity]?.evidence?.(r) : null;
        return (
          /*
           * The role is the app-wide `RoleBadge`, beside the name as everywhere else.
           *
           * A hand-rolled gold "Head" tag marked ONE of the two roles on this board, so a Sales
           * Person's row said nothing about their role and read as the unlabelled default — on a
           * page whose whole point is comparing the two. `RoleBadge` gives every row its own
           * coloured pill (SH gold, SP green) and carries the full role as its accessible name.
           */
          <span style={{ fontWeight: 600, color: token.colorPrimary }}>
            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
              <span className="min-w-0 break-words">{v}</span>
              <RoleBadge role={r.role} />
            </span>
            {why && (
              <span className="mt-1 block type-label font-normal text-danger-600 dark:text-danger-400">{why}</span>
            )}
          </span>
        );
      } },
    /*
     * A REJECTED order is terminal — refused business — so it is NOT counted in any figure on this
     * page. The count used to carry a muted "+N" of the rejected ones beside it; that was a second
     * number competing with the one that matters, on a page whose whole job is the counted figure.
     */
    // Where this rep works. A beat is non-exclusive and a Head covers a zone, so this is a LIST;
    // the first is their primary. Coverage stand-ins are excluded — see the server query.
    { title: 'Territory', dataIndex: 'territories', sorter: true, width: 150,
      render: (v: string[] = []) => (v.length ? (
        <Tooltip title={v.length > 1 ? v.join(' · ') : undefined}>
          <span>
            {v[0]}
            {v.length > 1 && <span style={{ color: token.colorTextTertiary }}> +{v.length - 1}</span>}
          </span>
        </Tooltip>
      ) : <span style={{ color: token.colorTextTertiary }}>—</span>) },
    { title: 'Orders', dataIndex: 'orders', align: 'right', width: 90, sorter: true },
    { title: 'Order value', dataIndex: 'orderValue', align: 'right', width: 130, sorter: true,
      render: (v: number) => inr(v) },
    /* The brand green and the bold are a VERDICT — money that arrived. Nothing arrived at ₹0, so it
       is printed plainly; nine of twelve rows wearing the success colour made the colour meaningless
       and flattered every rep who has invoiced nothing. */
    { title: 'Realized rev.', dataIndex: 'realizedRevenue', align: 'right', width: 130, sorter: true,
      render: (v: number, r: any) => (Number(v) > 0
        ? (
          <Tooltip title={Number(r.orderValue) > 0 ? `${shareOf(v, r.orderValue)} of the ${inr(r.orderValue)} they booked has reached an invoice.` : undefined}>
            <b style={{ color: PALETTE.brand[600] }}>{inr(v)}</b>
          </Tooltip>
        )
        : (
          <Tooltip title={Number(r.orderValue) > 0 ? `${inr(r.orderValue)} booked, none of it invoiced yet.` : 'Nothing booked in this period, so nothing to invoice.'}>
            <span style={{ color: token.colorTextTertiary }}>{inr(0)}</span>
          </Tooltip>
        )) },
    /* An average over NO orders is not ₹0 — it does not exist. Printing ₹0 said "their orders are
       worth nothing" about a rep who simply has none, and it sorted them alongside real zeroes.
       The server sends null now, and a dash is the honest rendering of it. */
    { title: 'Avg order', dataIndex: 'avgOrderValue', sorter: true, align: 'right', width: 110,
      render: (v: number | null, r: any) => (v == null
        ? <Tooltip title="No orders in this period, so there is no average to take."><span style={{ color: token.colorTextTertiary }}>—</span></Tooltip>
        : <Tooltip title={`${inr(r.orderValue)} across ${num(r.orders)} order(s).`}><span>{inr(v)}</span></Tooltip>) },
    /* What the Visit Scheduler ASKED of this rep, beside what they DID. The pair is the sentence a
       Sales Head needs ("planned 116, completed 82"); either number alone is unreadable. */
    { title: 'Planned', dataIndex: 'plannedVisits', align: 'right', width: 100,
      sorter: true,
      render: (v: number) => (Number(v) > 0
        ? <Tooltip title="Visits the scheduler planned for this rep in the period (cancelled stops excluded)."><span>{num(v)}</span></Tooltip>
        : <Tooltip title="No dated plan for this rep in the period."><span style={{ color: token.colorTextTertiary }}>—</span></Tooltip>) },
    /* CALLS MADE. Every completed visit is one, so three calls on the same dealer are three — which
       is exactly how this differs from Customers covered beside it, and the tooltip says so. The
       header used to read just "Visits" while the column two to the right read "Visits done" and
       counted customers; a row showing 4 here and 2 there looked like two answers to one question. */
    { title: 'Visits done', dataIndex: 'visits', align: 'right', width: 100,
      // Sorted by what the cell PRINTS (completed), not by the raw `visits` it is keyed on.
      sorter: true,
      render: (v: number, r: any) => {
        const done = Number(r.visitsCompleted ?? v);
        const open = Math.max(0, v - done);
        const parts = [`${num(done)} completed visit(s) — repeat calls on the same customer each count as one`];
        if (open > 0) parts.push(`${num(open)} still open`);
        if (r.visitsCancelled > 0) parts.push(`${num(r.visitsCancelled)} set off for but never reached`);
        parts.push('Customers visited counts the PEOPLE behind these calls, so it is never larger.');
        return (
          <Tooltip title={parts.join(' · ')}>
            <span>{num(done)}</span>
          </Tooltip>
        );
      } },
    /*
     * CUSTOMERS COVERED — the visit rule MET for the window, the meaning Visits & Coverage gives it
     * (owner decision, 15 Sep). The server asks the coverage engine for it, read as at the end of the
     * period, so this column and Coverage → By staff show the same pair for the same person.
     *
     * It used to count customers seen at least ONCE in the period — a different fact under the same
     * name, which is how a rep could read "1 / 1 · 100%" here while Coverage said the dealer was owed
     * two more calls. That fact is still on the board, named for what it is: Customers visited, next.
     */
    { title: 'Customers covered', dataIndex: 'coveragePct', align: 'right', width: 160,
      sorter: true,
      render: (v: number | null, r: any) => {
        if (v == null) {
          return (
            <Tooltip title="No customer assigned to this staff member has a visit rule for this window, so nothing is owed yet.">
              <span style={{ color: token.colorTextTertiary }}>—</span>
            </Tooltip>
          );
        }
        const done = Number(r.customersCovered ?? 0);
        const due = Number(r.customers ?? 0);
        const remaining = Math.max(0, due - done);
        return (
          <Tooltip title={`${num(done)} of ${num(due)} customers with a visit rule have had every call their rule asks for this window — ${num(v)}% · ${num(remaining)} still owed calls. The same figure as Visits & Coverage → By staff, read at the end of the period. Exempt customers are left out.`}>
            <span className="whitespace-nowrap">
              <span style={{ color: done >= due && due > 0 ? PALETTE.brand[600] : undefined, fontWeight: done >= due && due > 0 ? 600 : 400 }}>{num(done)}</span>
              <span style={{ color: token.colorTextTertiary }}> / {num(due)}</span>
              <Tag tone={v >= 80 ? 'green' : v >= 40 ? 'gold' : 'red'} style={{ marginInlineStart: 6 }}>{v}%</Tag>
            </span>
          </Tooltip>
        );
      } },
    /*
     * CUSTOMERS VISITED — how many DISTINCT customers on this rep's book they reached at least once in
     * the period, over the book. Reach, not a met rule: one call on a twice-a-month dealer is a visit
     * and not coverage. Calls on another rep's or nobody's customers are named beside it, never folded
     * into the share (that is what once printed "2 / 117" and "2 / 1").
     */
    { title: 'Customers visited', dataIndex: 'customersVisited', align: 'right', width: 150,
      sorter: true,
      render: (_: any, r: any) => {
        const book = Number(r.bookCustomers ?? 0);
        const seen = Number(r.customersVisitedOnBook ?? 0);
        const off = Number(r.customersOffBook ?? 0);
        if (!book && !off) {
          return (
            <Tooltip title="No retailers or farmers are on this staff member's book.">
              <span style={{ color: token.colorTextTertiary }}>—</span>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={[
            `${num(seen)} of ${num(book)} customers on their own book were visited at least once in this period.`,
            off > 0 ? `${num(off)} more customer(s) called on who are on another staff member's book or on nobody's — real work, counted separately, never inside the share.` : '',
            'Repeat calls on one customer count once; Visits done counts the calls.',
          ].filter(Boolean).join(' ')}>
            <span>
              <span className="whitespace-nowrap">
                <span>{num(seen)}</span>
                <span style={{ color: token.colorTextTertiary }}> / {num(book)}</span>
              </span>
              {off > 0 && (
                <div className="text-2xs whitespace-nowrap" style={{ color: token.colorTextTertiary }}>
                  +{num(off)} off book
                </div>
              )}
            </span>
          </Tooltip>
        );
      } },
    /* HOW WELL those visits were executed — the shared score in docs/VISIT-DATA-CONTRACT.md §5.
       Null, never 0, for a rep who completed none: an unmeasured rep is not a poor one. */
    { title: 'Quality', dataIndex: 'qualityPct', align: 'right', width: 105,
      sorter: true,
      render: (v: number | null) => (v == null
        ? <Tooltip title="No completed visits in this period — nothing to score."><span style={{ color: token.colorTextTertiary }}>—</span></Tooltip>
        : (
          <Tooltip title="Average visit quality: GPS verified, photo, duration, feedback and order — weighted toward evidence that the call really happened.">
            <Tag tone={v >= 75 ? 'green' : v >= 50 ? 'gold' : 'red'}>{v}%</Tag>
          </Tooltip>
        )) },
    // Shown as the PAIR — "1 / 2" — not the decimal it divides to. A lone "1.0" reads as a score out
    // of one; the pair states the two counts it is made of. The ratio still drives colour and sort.
    // The heading names the RETAILER visit, because that is the only kind in the denominator: a
    // farmer call cannot produce a dealer order and would only drag the figure down. Titled plainly
    // "Orders / visits" it read as if it divided by the Visits done column beside it, which counts
    // farmer calls too — two different numbers under one word. This is also the label the exported
    // file already uses, so the screen and the spreadsheet now name the metric identically.
    { title: 'Orders / retailer visit', dataIndex: 'ordersPerRetailerVisit', align: 'right', width: 150,
      sorter: true,
      render: (v: number | null, r: any) => {
        const ord = Number(r.orders ?? 0);
        const rv = Number(r.retailerVisits ?? 0);
        /*
         * A dash only when NOTHING happened. This cell is a PAIR, and a pair can be printed whenever
         * either half is non-zero — the ratio being undefined is not a reason to hide the numerator.
         * It used to render "—" whenever `ordersPerRetailerVisit` was null, so a rep with EIGHT
         * orders and no retailer visit showed a dash: the page dropped eight orders on the floor in
         * the one column whose job is to relate orders to visits. "8 / 0" is the true and useful
         * reading — every one of those orders came from somewhere other than a call.
         */
        if (!ord && !rv) {
          return (
            <Tooltip title="No orders and no completed retailer visits in this period."><span style={{ color: token.colorTextTertiary }}>—</span></Tooltip>
          );
        }
        const tone = v == null ? 'neutral' : v >= 1 ? 'green' : v >= 0.5 ? 'gold' : 'neutral';
        return (
          <Tooltip title={rv === 0
            ? `${num(ord)} order(s) and no completed retailer visit — every one of them was raised by phone, from the office or in bulk rather than on a call. There is no visit to divide by, so this is the pair, not a rate.`
            : `${num(ord)} order(s) against ${num(rv)} completed RETAILER visit(s) — farmer calls are not in the denominator, so this can be fewer than Visits done. Orders raised by phone or from the office count too, so the first number can be the larger one.`}>
            <Tag tone={tone}>{pairOf(ord, rv)}</Tag>
          </Tooltip>
        );
      } },
    /*
     * The coverage gap — "3 of 4 covered, 1 to go" — used to live here as a SECOND column, printing
     * the same pair the Coverage percentage was made from. It now lives in the single "Customers
     * covered" column above, which carries the pair AND the share. Three visits is good work against
     * a book of five and nothing at all against a book of two thousand; one cell says both.
     */
    { title: 'Present', dataIndex: 'daysPresent', align: 'right', width: 90, sorter: true },
    { title: 'Absent', dataIndex: 'absentDays', align: 'right', width: 90, sorter: true,
      render: (v: number, r: any) => (
        <Tooltip title={`${r.halfDays} half-day(s) · ${r.expectedDays} working days expected (Sundays, company holidays and approved leave excluded)`}>
          <span style={{ fontWeight: v > 0 ? 600 : 400, color: v > 0 ? token.colorError : token.colorTextTertiary }}>{v}</span>
        </Tooltip>
      ) },
    /* A red "0%" against a rep with NO working days expected (joined mid-period, or every day off)
       is a verdict on a denominator of nothing. The percentage is only a percentage when something
       was expected; otherwise say so. */
    { title: 'Attend. %', dataIndex: 'attendancePct', align: 'right', width: 100, sorter: true,
      render: (v: number, r: any) => (Number(r.expectedDays) > 0
        ? (
          <Tooltip title={`${num(r.daysPresent)} present${Number(r.halfDays) > 0 ? ` + ${num(r.halfDays)} half-day(s)` : ''} of ${num(r.expectedDays)} working day(s) expected.`}>
            <Tag color={v >= 90 ? 'green' : v >= 70 ? 'gold' : 'red'}>{v}%</Tag>
          </Tooltip>
        )
        : (
          <Tooltip title="No working days were expected of this staff member in this period (Sundays, company holidays and approved leave are excluded), so there is no attendance to measure.">
            <span style={{ color: token.colorTextTertiary }}>—</span>
          </Tooltip>
        )) },
    /* DECLARED distance. When nothing was declared but the GPS trace recorded movement, the bare
       dash hid the one fact worth seeing on a work-integrity page — that the rep travelled and did
       not claim it. The dash still says "nothing declared"; the GPS figure sits under it. */
    { title: 'Distance', dataIndex: 'declaredKm', align: 'right', width: 110, sorter: true,
      render: (v: number, r: any) => {
        const gps = Number(r.gpsKm ?? 0);
        if (Number(v) > 0) {
          return (
            <Tooltip title={gps > 0 ? `Declared on expense claims. GPS trace supports ${km(gps)}.` : 'Declared on expense claims. No GPS trace exists to verify it.'}>
              <span>{km(v)}</span>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={gps > 0
            ? `Nothing declared on an expense claim, but the GPS trace recorded ${km(gps)} of travel in this period.`
            : 'Nothing declared on an expense claim, and no GPS trace exists for this period.'}>
            <span style={{ color: token.colorTextTertiary }}>
              —
              {gps > 0 && <div className="text-2xs">{km(gps)} by GPS</div>}
            </span>
          </Tooltip>
        );
      } },
    ...(canSeeMoney ? [{
      title: 'Expenses', dataIndex: 'expenseClaimed', align: 'right' as const, width: 120,
      sorter: true,
      render: (v: number, r: any) => (
        <Tooltip title={`${inr(r.expensePaid)} already settled`}>
          <span style={{ color: v > 0 ? undefined : token.colorTextTertiary }}>{v > 0 ? inr(v) : '—'}</span>
        </Tooltip>
      ),
    }] : []),
    { title: 'Flags', dataIndex: 'anomalyDays', align: 'center', width: 110, sorter: true,
      render: (v: number, r: any) => <FlagSummary days={v} counts={r.flagCounts} severe={r.noWorkClaimedDays > 0} muted={token.colorTextTertiary} /> },
    { title: 'New retailers', dataIndex: 'newRetailers', sorter: true, align: 'right', width: 120 },
    { title: 'New farmers', dataIndex: 'newFarmers', sorter: true, align: 'right', width: 110 },
  ];

  /** The order in words, named with the COLUMN HEADING the reader clicked — for the export dialog. */
  const orderWords = (() => {
    const key = sortState?.key ?? 'orders';
    const dir = sortState?.dir ?? 'desc';
    const title = columns.find((c) => (c.key ?? c.dataIndex) === key)?.title;
    const isText = key === 'name' || key === 'territories';
    return `${typeof title === 'string' ? title : key}, ${isText
      ? (dir === 'desc' ? 'Z to A' : 'A to Z')
      : (dir === 'desc' ? 'highest first' : 'lowest first')}`;
  })();

  return (
    <div>
      <Typography.Title level={1} style={{ marginBottom: 2 }}>Sales Performance</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Orders, revenue, visits, attendance and field movement per field staff (Sales Person + Sales Head). Pick a period to compare any day, month, year or custom range.
      </Typography.Paragraph>

      {/* Period controls on the left, the export on the right — the same shape as every other list
          page's toolbar, and the export is an outlined action like the rest of them. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Segmented
          value={preset}
          onChange={(v) => setPreset(v as Preset)}
          options={[
            { label: 'Today', value: 'today' }, { label: 'This week', value: 'week' },
            { label: 'This month', value: 'month' }, { label: 'This year', value: 'year' },
            { label: 'Custom', value: 'custom' },
          ]}
        />
        {preset === 'custom' && (
          <DatePicker.RangePicker disabledDate={disableFuture}
            value={custom as any}
            onChange={(v) => setCustom(v as [Dayjs, Dayjs] | null)}
            allowClear={false}
          />
        )}
        <Typography.Text type="secondary" className="text-xs">{formatIstDate(from)} → {formatIstDate(to)}</Typography.Text>
        <Button className="ml-auto" size="sm" variant="default" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
      </div>

      {/* Filters, one per column and in the same order as the columns — reading the toolbar
          left-to-right walks the header left-to-right. The KPI band below sets the third one. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          size="sm"
          allowClear
          prefix={<Search size={14} className="text-subtle" />}
          placeholder="Staff name" aria-label="Staff name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 210 }}
        />
        <Select
          size="sm"
          allowClear
          placeholder="Any role"
          value={role}
          onChange={(v: any) => setRole(v ?? null)}
          options={[{ label: 'Sales Person', value: 'SALES_PERSON' }, { label: 'Sales Head', value: 'SALES_HEAD' }]}
          style={{ width: 150 }}
        />
        <TerritoryCascade facets={data?.facets?.territories ?? []} pick={territory} set={setTerritory} />
        {/* Then the measured-value filters, still in column order: Customers covered, Attend. %,
            Flags. Each is the band of a column the reader can already see, and each option carries
            the staff it would leave — so nothing here can be picked into an empty table. */}
        <FacetSelect
          placeholder="Any coverage" value={coverage} onChange={setCoverage}
          options={COVERAGE_BANDS} counts={facets.coverage ?? {}} width={195}
        />
        <FacetSelect
          placeholder="Any attendance" value={attendance} onChange={setAttendance}
          options={ATTENDANCE_BANDS} counts={facets.attendance ?? {}} width={195}
        />
        <FacetSelect
          placeholder="Any flag" value={flag} onChange={setFlag}
          options={flagOptions} counts={facets.flags ?? {}} width={185}
        />
        {/* The tile filter has no control of its own — it is set by clicking a KPI — so it gets a
            removable chip here, or it would be invisible state. */}
        {activity && (
          <span className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-surface-2 py-1 pl-2.5 pr-1.5 text-xs text-content">
            {ACTIVITY_LABEL[activity] ?? activity}
            <button
              type="button"
              aria-label="Remove filter"
              onClick={() => setActivity(null)}
              className="grid size-4 place-items-center rounded-pill text-subtle transition-colors hover:bg-surface-3 hover:text-content"
            >
              <XIcon size={12} />
            </button>
          </span>
        )}
        {anyFilter && (
          <FilterResetButton onClick={clearFilters} />
        )}
        <Typography.Text type="secondary" className="ml-auto text-xs">
          {/* "0 of 0" is a measurement. Print it only when one was actually taken. */}
          {loadFailed ? 'Field staff not loaded'
            : anyFilter ? `${rows.length} of ${staffTotal} field staff` : `${staffTotal} field staff`}
        </Typography.Text>
      </div>

      {/* A drill that selected on something the columns do not show has to SAY what it selected on.
          Otherwise the reader is left with a row whose every visible figure argues against the
          filter that produced it — 17 orders and 4 visits under a filter named "no work". */}
      {!loadFailed && activity && LANE_BASIS[activity] && (
        <div className="mb-4 rounded-control border border-line bg-surface-2 px-3.5 py-2.5 text-sm shadow-e1">
          <div className="type-strong">
            Showing {rows.length === 1 ? 'the 1 field staff member' : `the ${num(rows.length)} field staff`} whose period contains this — {ACTIVITY_LABEL[activity]?.toLowerCase()}
          </div>
          <div className="mt-0.5 type-body opacity-90">{LANE_BASIS[activity].words}</div>
          {LANE_BASIS[activity].day && (
            <div className="mt-0.5 type-body opacity-90">
              It is a fact about particular DAYS, not about the period totals in the columns — open a row to see which days.
            </div>
          )}
        </div>
      )}

      {/* The band is absent on failure because there is nothing to put in it — so the reason goes
          here, where the band would have been, rather than leaving the reader to infer it from a
          page that looks merely empty. */}
      {loadFailed && (
        <Alert
          className="mb-4"
          type="error"
          icon={<TriangleAlert />}
          title="Could not load the performance board"
          description="Your filters are still applied — this is a loading failure, not an empty result."
          action={<Button size="sm" variant="default" onClick={() => refetch()}>Try again</Button>}
        />
      )}

      {totals && (
        <div className={cn('mb-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5', canSeeMoney ? 'xl:grid-cols-9' : 'xl:grid-cols-8')}>
          <Kpi
            label="Field staff" value={num(totals.salesPersons)}
            // The tile body and its sub-line answer two DIFFERENT questions — "who is in scope?" and
            // "who actually worked?" — so they drill separately. Clicking 12 clears the filters and
            // shows all of them; clicking "5 did work" shows exactly those 5.
            sub={<SubLink text={`${num(totals.staffActive ?? 0)} did work`} filter="active" count={staffCounts.active} color={PALETTE.sky[500]} />}
            color={PALETTE.sky[500]}
            hint={`Every field staff member in your scope for this period. ${num(totals.staffActive ?? 0)} of them recorded a visit, an order or a new customer. Click the tile to clear every filter, or "did work" to show just those ${num(totals.staffActive ?? 0)}.`}
          />
          <Kpi
            label="Orders" value={num(totals.orders)}
            sub={`${num(staffCounts.orders ?? 0)} of ${num(totals.salesPersons)} staff`}
            color={PALETTE.plum[500]} filter="orders" count={staffCounts.orders}
            // Rejected orders are excluded — that is a definition, not a running tally worth printing.
            hint={`${num(totals.orders)} order(s) placed, by ${num(staffCounts.orders ?? 0)} staff. Orders refused at approval are not counted. Click to show only the staff who placed one.`}
          />
          <Kpi
            label="Order value" value={inr(totals.orderValue)}
            sub={totals.avgOrderValue == null ? 'no orders yet' : `${inr(totals.avgOrderValue)} avg order`}
            color={PALETTE.warning[600]} filter="orderValue" count={staffCounts.orderValue}
            hint={`Value of every order placed in the period. Click to show only the staff who booked value.`}
          />
          <Kpi
            label="Realized rev." value={inr(totals.realizedRevenue)}
            sub={Number(totals.orderValue) > 0 ? `${shareOf(totals.realizedRevenue, totals.orderValue)} of order value` : 'nothing invoiced'}
            color={PALETTE.brand[600]} filter="revenue" count={staffCounts.revenue}
            hint="Order value that reached an invoice — read from the invoices themselves, cancelled ones excluded. Billed, not collected. Click to show only the staff who realized revenue."
          />
          {/* The Visits done COLUMN sums to `visitsCompleted`, so that is what this tile must show:
              a tile that prints a number no column on the page adds up to is a tile the reader
              cannot reconcile. `visits` also counts calls still open, which the column excludes. */}
          <Kpi
            label="Visits done" value={num(totals.visitsCompleted ?? totals.visits)}
            // "not reached" needs its OWN drill, not the tile's: those visits carry no check-in, so
            // the tile's own `visits > 0` lane is the one set of staff guaranteed to exclude a rep
            // who set off and arrived nowhere. Clicking the tile would have hidden them.
            sub={
              <>
                {`${num(totals.retailerVisits)} retailer`}
                {totals.visitsCancelled > 0 && (
                  <>
                    {' · '}
                    <SubLink text={`${num(totals.visitsCancelled)} not reached`} filter="notReached" count={staffCounts.notReached} color="#08979C" />
                  </>
                )}
              </>
            }
            color="#08979C" filter="visits" count={staffCounts.visits}
            hint={`${num(totals.visitsCompleted ?? totals.visits)} completed visit(s), ${num(totals.retailerVisits)} of them to retailers${totals.visitsCancelled > 0 ? `, and ${num(totals.visitsCancelled)} set off for but never reached — click "not reached" for those staff` : ''}. These are CALLS: repeat calls on one customer each count. Click the tile to show only the staff who completed one.`}
          />
          {/* Customers, not visits. The label said "Visits pending" while the figure counted people
              on a book — the same word doing two jobs one column apart. */}
          <Kpi
            label="Customers to cover" value={num(totals.visitsPending ?? 0)}
            sub={totals.customers ? `${num(totals.customersCovered ?? 0)} of ${num(totals.customers)} covered` : 'no visit rule applies'}
            color="#D46B08" filter="pending" count={staffCounts.pending}
            hint={totals.customers
              ? `${num(totals.visitsPending)} customer(s) with a visit rule have not yet had every call their rule asks for this window — ${num(totals.customersCovered ?? 0)} of ${num(totals.customers)} have (the same count as Visits & Coverage, read at the end of the period). ${num(totals.customersVisitedOnBook ?? 0)} of ${num(totals.bookCustomers ?? 0)} customers on field-staff books were visited at least once in the period. Click to show only the staff who still have customers to cover.`
              : 'No customer assigned to field staff in scope has a visit rule for this window.'}
          />
          <Kpi
            label="New customers" value={num((totals.newRetailers ?? 0) + (totals.newFarmers ?? 0))}
            sub={`${num(totals.newRetailers)} retailers · ${num(totals.newFarmers)} farmers`}
            color="#C41D7F" filter="newCustomers" count={staffCounts.newCustomers}
            hint="Retailers and farmers first recorded by this staff in the period. Click to show only the staff who added one."
          />
          {canSeeMoney && (
            <Kpi
              label="Expenses" value={inr(totals.expenseClaimed)}
              sub={`${inr(totals.expensePaid)} settled`}
              color="#0958D9" filter="expenses" count={staffCounts.expenses}
              hint={`${inr(totals.expenseClaimed)} claimed · ${inr(totals.expensePaid)} already settled. Click to show only the staff who claimed.`}
            />
          )}
          <Kpi
            label="Flagged days" value={num(totals.anomalyDays)}
            // The money-costing subset gets its own drill. The tile filters to everyone carrying ANY
            // flag, which on this data is 7 days across several staff — reaching the one that was
            // CLAIMED against meant reading every flagged row to find it again.
            sub={totals.noWorkClaimedDays > 0
              ? <SubLink text={`${num(totals.noWorkClaimedDays)} claimed, no work`} filter="noWorkClaimed" count={staffCounts.noWorkClaimed} color={PALETTE.danger[600]} />
              : `${num(staffCounts.flagged ?? 0)} of ${num(totals.salesPersons)} staff`}
            color={totals.noWorkClaimedDays > 0 ? PALETTE.danger[600] : totals.anomalyDays > 0 ? PALETTE.warning[600] : '#8C8C8C'}
            filter="flagged" count={staffCounts.flagged}
            hint={totals.noWorkClaimedDays > 0
              ? `${totals.anomalyDays} flagged day(s) — ${totals.noWorkClaimedDays} of them had expenses claimed against a day with no work recorded. Click the tile for every flagged staff member, or "claimed, no work" for just those.`
              : `${totals.anomalyDays} day(s) with a work-integrity flag. Click to show only the staff carrying one.`}
          />
        </div>
      )}

      <Card density="compact">
        <Table
          rowKey="salesPersonId"
          size="small"
          loading={isFetching}
          dataSource={rows}
          columns={columns}
          scroll={{ x: canSeeMoney ? 2000 : 1880 }}
          onRow={(r: any) => ({ onClick: () => setDrillId(r.salesPersonId), style: { cursor: 'pointer' } })}
          // The header reports up and the server returns the whole set in order — so the order is a
          // fact about the request, which an export can be handed too.
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} field staff` }}
          locale={{ emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              // Three different situations, three different sentences. The failure case used to
              // borrow the filter case's wording and blame the reader's filters for a table the
              // server never sent.
              description={loadFailed ? 'The board could not be loaded — this is not an empty result'
                : anyFilter ? 'No field staff match these filters' : 'No field staff in scope'}
            />
          ) }}
        />
      </Card>

      {drillId && (
        <PerfModal
          spId={drillId} from={from} to={to} onClose={() => setDrillId(null)}
          // Open ON the evidence. A reader who drilled "claimed, no work" and then clicked the row
          // was handed the rep's whole period and left to find the offending day themselves — the
          // third place in a row where the figure and its proof were not connected.
          initialDay={activity ? LANE_BASIS[activity]?.day : undefined}
          // Lets the drill-down widen the period to fill its month table WITHOUT closing itself —
          // otherwise "compare months" means: close, find the period control, re-open the person.
          onCompareMonths={() => {
            setPreset('custom');
            setCustom([dayjs().subtract(11, 'month').startOf('month'), dayjs()]);
          }}
        />
      )}

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        params={boardParams}
        scope={scopeWords}
        order={orderWords}
        staffCount={rows.length}
      />
    </div>
  );
}

/**
 * THE EXPORT IS THE SCREEN.
 *
 * This dialog used to carry its own period, role, territory and a tick list of every staff member —
 * a second, parallel filter set sitting in front of a page that was already filtered. The two could
 * disagree, and did: a board narrowed to one district exported the whole company, because the dialog
 * opened with its own filters blank and nothing said so. Answering "who is in this file?" meant
 * re-deriving it from controls that had no relationship to the ones behind them.
 *
 * So it carries no filter at all. It ships the page's period, every filter the page has applied and
 * the column the table is ordered by, and prints that scope back — the server re-runs the very same
 * leaderboard call (see `salesExport`), so the file cannot be a different report than the screen it
 * came from. Changing what is exported means changing the page, which is where a filter belongs.
 *
 * Two choices are left, because they are choices about the FILE and not about the data: which format,
 * and whether to carry the days behind the totals.
 */
function ExportDialog({ open, onClose, params, scope, order, staffCount, only }: {
  open: boolean; onClose: () => void;
  /** The board's own query, verbatim. */
  params: Record<string, string | undefined>;
  /** That query in words. Empty means nothing is filtered — which is itself worth saying. */
  scope: string[];
  /** The column the table is ordered by, in words ("Orders, highest first"). */
  order: string;
  /** How many staff the file will cover — the row count the screen is showing. */
  staffCount: number;
  /** Locks the export to ONE person — the drill-down's own export button. Same dialog, so a per-rep
   *  report is not a second, weaker export path that quietly lacks PDF. */
  only?: { id: string; name: string } | null;
}) {
  const [format, setFormat] = useState<'xlsx' | 'pdf'>('xlsx');
  const [detail, setDetail] = useState(true);
  const [busy, setBusy] = useState(false);

  const f = String(params.from ?? '');
  const t = String(params.to ?? '');
  const days = Math.max(1, dayjs(t).diff(dayjs(f), 'day') + 1);

  const run = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v != null && v !== '') qs.set(k, String(v));
      qs.set('format', format);
      if (detail) qs.set('detail', '1');
      const url = `/performance/sales/export?${qs.toString()}`;
      const slug = (s: string) => s.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
      // The file says in its own name whether it is the whole scope or a slice of it, so a filtered
      // export cannot be forwarded as "the company's numbers".
      const who = only ? slug(only.name) : scope.length ? 'filtered' : 'all-staff';

      if (format === 'pdf') {
        /*
         * PREVIEW, don't download. A report is read before it is kept: forcing a file into Downloads
         * just to find out whether the period and the staff were right leaves a trail of
         * near-identical PDFs. The browser's own viewer carries Save and Print, so nothing is lost.
         *
         * `previewPdf` opens the tab SYNCHRONOUSLY (before the await) or the pop-up blocker eats it.
         */
        await previewPdf(() => api.get(url, { responseType: 'blob' }));
        message.success('Report opened in a new tab');
      } else {
        const res = await api.get(url, { responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `sales-performance_${who}_${f}_to_${t}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(objectUrl); a.remove(); }, 100);
        message.success('Export downloaded');
      }
      onClose();
    } catch (e: any) {
      // The server refuses a detail request that would run to thousands of rows, and says how many —
      // pass that straight through rather than replacing it with a vague apology.
      let msg = 'Could not export. Narrow the period or the filters on the page, then try again.';
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try { msg = JSON.parse(await blob.text())?.error?.message ?? msg; } catch { /* keep the default */ }
      } else if (blob?.error?.message) {
        msg = blob.error.message;
      }
      message.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o: boolean) => { if (!o) onClose(); }}
      title={only ? `Export · ${only.name}` : 'Export sales performance'}
      description="The file is what you are looking at. Choose the format and how much detail."
      width={620}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary" loading={busy} disabled={!only && !staffCount} onClick={run}
            icon={format === 'pdf' ? <Eye size={16} /> : <Download size={16} />}
          >
            {format === 'pdf' ? 'Preview PDF' : 'Export Excel'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* What is about to leave the building, in the words the toolbar used — read-only, because
            every one of these has a control on the page behind this dialog. */}
        <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5">
          <div className="type-label text-muted">This report covers</div>
          <div className="mt-1 type-body-medium text-content">
            {formatIstDate(f)} → {formatIstDate(t)}
            <span className="text-muted"> · {days} day{days === 1 ? '' : 's'}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-sm text-muted">
            <UserPlus size={14} className="text-subtle" />
            {only ? only.name : `${num(staffCount)} field staff`}
          </div>
          {!only && (
            <>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {scope.length ? scope.map((s) => (
                  <span key={s} className="rounded-pill border border-line bg-surface px-2 py-0.5 text-xs text-content">{s}</span>
                )) : (
                  <span className="text-xs text-muted">No filters applied — everyone in your scope.</span>
                )}
              </div>
              <div className="mt-2 text-xs text-muted">
                Ordered by {order}, exactly as the table is showing it. To export something else,
                close this and change the period or the filters on the page.
              </div>
            </>
          )}
        </div>

        <XField label="Format">
          <Segmented
            value={format}
            onChange={(v) => setFormat(v as 'xlsx' | 'pdf')}
            options={[
              { label: 'Excel', value: 'xlsx' },
              { label: 'PDF', value: 'pdf' },
            ]}
          />
        </XField>

        <XField label="How much detail">
          <Segmented
            value={detail ? 'full' : 'summary'}
            onChange={(v) => setDetail(v === 'full')}
            options={[
              { label: 'Summary only', value: 'summary' },
              { label: 'Full — day by day', value: 'full' },
            ]}
          />
          <div className="mt-1 text-xs text-muted">
            {!detail
              ? 'One row per person: the totals you see in the table.'
              : format === 'xlsx'
                ? 'A sheet per person — their headline numbers, every day of the period, and what was flagged — plus every day in date order, the same days by staff, and a flags sheet.'
                : 'The leaderboard, then every day in date order, then a dedicated report per person with what each flag means.'}
          </div>
        </XField>
      </div>
    </Modal>
  );
}
function XField({ label, children }: { label: string; children: any }) {
  return <div><div className="mb-1 type-label text-muted">{label}</div>{children}</div>;
}

/** One compact chip for a rep's flagged days — a count, not a wall of tags. The breakdown lives in
 *  the tooltip so the table stays readable; the day-by-day grid in the drawer has the detail. */
function FlagSummary({ days, counts, severe, muted }: { days: number; counts?: Record<string, number>; severe: boolean; muted: string }) {
  if (!days) return <span style={{ color: muted }}>—</span>;
  const lines = Object.entries(counts ?? {})
    .sort((a, b) => b[1] - a[1])
    .map(([f, n]) => `${flagMeta(f).label} · ${n} day${n === 1 ? '' : 's'}`)
    .join('\n');
  return (
    <Tooltip title={lines || undefined}>
      <Tag tone={severe ? 'red' : 'gold'}>{days} day{days === 1 ? '' : 's'}</Tag>
    </Tooltip>
  );
}

/** The day grid's filter set. Every key is applied by the SERVER — see `DayFilters` in the
 *  performance module — so the grid, its counts and its export can never drift apart. */
type DayFilter = { status?: string; has?: string; flag?: string };

/** Shape of the server's day-level counts, zeroed. Rendering the tiles against this while the first
 *  fetch is in flight keeps the modal from reflowing the moment the numbers land. */
const EMPTY_DAY_TOTALS = {
  days: 0, onDuty: 0, flagged: 0, flaggedHigh: 0,
  has: { orders: 0, revenue: 0, visits: 0, newCustomers: 0, claims: 0, route: 0, noWork: 0 } as Record<string, number>,
  statusCounts: {} as Record<string, number>, flagCounts: {} as Record<string, number>,
  orders: 0, ordersRejected: 0, orderValue: 0, realized: 0, visits: 0, visitsCompleted: 0, visitsCancelled: 0,
  newRetailers: 0, newFarmers: 0, claimed: 0, paid: 0, declaredKm: 0, gpsKm: 0,
};

/**
 * One rep's period, opened from a leaderboard row.
 *
 * A MODAL rather than a slide-over: this is a page's worth of reading — seven KPIs, an attendance
 * meter, a filterable day grid and two charts — and a 1040px drawer pinned to the right edge left the
 * grid scrolling sideways inside a panel that was itself scrolling. Centred and wide, everything is
 * legible at once, and the day grid gets the width its twelve columns need.
 */
function PerfModal({ spId, from, to, onClose, onCompareMonths, initialDay }: {
  spId: string; from: string; to: string; onClose: () => void; onCompareMonths?: () => void;
  /** Day filter the board's own drill implies — see `LANE_BASIS`. */
  initialDay?: DayFilter;
}) {
  const { token } = theme.useToken();
  const { data, isLoading } = useQuery({
    queryKey: ['performance', 'sales', spId, from, to],
    queryFn: () => request<any>({ url: `/performance/sales/${spId}`, params: { from, to } }),
  });
  const m = data?.metrics;
  const daily = data?.daily ?? [];

  const canSeeMoney = usePermission('EXPENSE.VIEW');

  // The day filters live HERE, not in the grid, because the KPI tiles above set them too: one piece
  // of state, one query, and the server decides what matches.
  const [f, setF] = useState<DayFilter>(initialDay ?? {});
  // The drill-down's own export. It opens the SAME dialog as the board's, locked to this person, so
  // a per-rep report gets the period picker, Excel/PDF and the PDF preview instead of a bare CSV.
  const [repExportOpen, setRepExportOpen] = useState(false);
  /**
   * Clicking a tile REPLACES the day filters rather than adding to them, so a tile reading "2" always
   * shows those 2.
   *
   * Stacking them silently is what produced "Flagged days 2 — why only 1 row?": a `has=distance`
   * filter set two clicks earlier was still narrowing the flagged days, and nothing on screen tied
   * the missing day to it. A tile that says 2 and yields 1 is a broken promise. Combinations still
   * belong to the selects below, where the state is visible and deliberate.
   */
  const toggle = (k: keyof DayFilter, v: string) =>
    setF((cur) => (cur[k] === v ? {} : ({ [k]: v } as DayFilter)));
  const dayQuery = useQuery({
    queryKey: ['performance', 'sales', spId, 'days', from, to, f],
    queryFn: () => request<any>({ url: `/performance/sales/${spId}/days`, params: { from, to, ...f } }),
    placeholderData: keepPreviousData,
  });
  const dt = { ...EMPTY_DAY_TOTALS, ...(dayQuery.data?.totals ?? {}) };

  /* Month-by-month for this rep. The board answers "how did September go"; it cannot answer "is he
     getting better", which is the question a manager actually asks of one person. Only fetched when
     the period spans more than one calendar month — for a single month the table would be one row
     restating the tiles above it. */
  const spansMonths = from.slice(0, 7) !== to.slice(0, 7);
  const monthsQuery = useQuery({
    queryKey: ['performance', 'sales', spId, 'months', from, to],
    queryFn: () => request<any>({ url: `/performance/sales/${spId}/months`, params: { from, to } }),
    enabled: spansMonths,
  });
  /**
   * A tile is only a filter when it has days to filter TO. `Has a new customer (0)` produced an empty
   * grid and the honest question "why is this empty?" — a control whose only possible outcome is
   * "nothing" should not be offered. Passing no `onClick` makes StatCard render as plain content.
   */
  const lens = (n: number, k: keyof DayFilter, v: string) => (n > 0 ? () => toggle(k, v) : undefined);
  // A Sales Person may only trace themselves (GPS.TRACE.SELF); heads/admins hold GPS.TRACE.VIEW.
  const canTraceOthers = usePermission('GPS.TRACE.VIEW');
  const canRunReports = usePermission('REPORT.RUN');
  const myId = useAuthStore((s) => s.user?.id);
  const canSeeMap = canTraceOthers || String(myId ?? '') === String(spId);
  const [mapDate, setMapDate] = useState<string | null>(null);

  // Chart hues flip with the theme. Both sets were validated against their own surface
  // (light #ffffff / dark #1f2621) for lightness band, chroma, CVD separation and contrast —
  // the brand-400 green that reads well on white sits outside the band on the dark surface.
  const isDark = useThemeStore((s) => s.resolved)() === 'dark';
  const C = isDark
    ? { money: PALETTE.brand[500], visits: PALETTE.sky[500], grid: token.colorBorderSecondary, axis: token.colorTextTertiary }
    : { money: PALETTE.brand[600], visits: PALETTE.sky[500], grid: token.colorBorderSecondary, axis: token.colorTextTertiary };

  // A series of nothing is not a chart. Plotting an all-zero line just draws a flat rule at the
  // baseline and invites the reader to hunt for data that isn't there — say so instead.
  const hasOrderValue = daily.some((d: any) => Number(d.value) > 0);
  const hasVisits = daily.some((d: any) => Number(d.visits) > 0);

  const tipStyle = {
    contentStyle: {
      background: token.colorBgElevated, border: `1px solid ${token.colorBorder}`,
      borderRadius: 8, fontSize: 12, color: token.colorText, boxShadow: '0 4px 16px rgba(0,0,0,.12)',
    },
    labelStyle: { color: token.colorTextSecondary, marginBottom: 2 },
    cursor: { fill: token.colorFillQuaternary },
  };

  /** A quiet supporting figure — grouped under one card rather than given a whole tile. */
  const Fact = ({ label, value, muted, to: href }: { label: string; value: string; muted?: boolean; to?: string }) => (
    <div className="min-w-0">
      <div className="truncate text-xs text-muted">{label}</div>
      {href && !muted ? (
        // The count opens the people it counts: the Customers Registered report for this rep and period.
        <Link to={href} title="See who they registered"
          className="mt-0.5 block type-section-title tabular-nums text-brand-700 hover:underline dark:text-brand-400">{value}</Link>
      ) : (
        <div className={`mt-0.5 type-section-title tabular-nums ${muted ? 'text-subtle' : 'text-content'}`}>{value}</div>
      )}
    </div>
  );
  // "Who did they register?" — the list behind the New retailers / New farmers counts.
  const registrationsHref = (kind: 'RETAILER' | 'FARMER') => (canRunReports
    ? `/reports?r=CUSTOMER_REGISTRATIONS&from=${from}&to=${to}&f.registeredBy=${spId}&f.customerType=${kind}`
    : undefined);

  const ChartCard = ({ title, total, has, height, children }: {
    title: string; total: string; has: boolean; height: number; children: React.ReactElement;
  }) => (
    <div className="rounded-card border border-line bg-surface shadow-e1 p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="type-body-strong text-content">{title}</span>
        <span className="text-xs tabular-nums text-muted">{total}</span>
      </div>
      {has ? (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center rounded-control bg-surface-2 text-xs text-subtle" style={{ height }}>
          Nothing recorded in this period
        </div>
      )}
    </div>
  );

  // Attendance is a part-to-whole, so it reads as one meter — three numbers in a row never showed
  // that 3 present against 3 absent IS the 50%. Every segment carries a visible label, which is also
  // what licenses the mid-tone gold (its contrast alone sits under 3:1).
  const present = Number(m?.daysPresent ?? 0);
  const half = Number(m?.halfDays ?? 0);
  const absent = Number(m?.absentDays ?? 0);
  const totalDays = present + half + absent;
  const seg = [
    { key: 'Present', n: present, color: isDark ? PALETTE.brand[500] : PALETTE.brand[600] },
    { key: 'Half day', n: half, color: isDark ? PALETTE.harvest[400] : PALETTE.harvest[500] },
    { key: 'Absent', n: absent, color: isDark ? '#ff7875' : PALETTE.danger[500] },
  ].filter((s) => s.n > 0);

  return (
    <Modal
      open
      onOpenChange={(o: boolean) => { if (!o) onClose(); }}
      width={1180}
      /* "Context · Subject" title: the dialog header is already semibold, so an extra <b> around the
         name would stack a second, heavier emphasis on top of it. Hierarchy comes from COLOUR —
         muted prefix, full-contrast subject, one shared weight. */
      title={<span><span className="text-muted">Performance ·</span> {data?.name ?? ''}</span>}
      description={`${formatIstDate(from)} → ${formatIstDate(to)}`}
    >
      {isLoading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : !m ? <Empty description="No data" /> : (
        <div className="space-y-4">
          {/* Every tile is a lens on the day grid below: clicking one keeps only the days it counts,
              clicking it again clears. The counts come from the server over the WHOLE period, so a
              selected tile never drags its neighbours toward zero. */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
            <StatCard tone="brand" icon={<ShoppingCart />} label="Orders" value={num(m.orders)}
              hint={m.orders
                ? `${inr(m.orderValue)} · ${inr(m.avgOrderValue)} avg · ${num(dt.has.orders)} day(s)`
                : 'none placed'}
              onClick={lens(dt.has.orders, 'has', 'orders')} active={f.has === 'orders'} />
            <StatCard tone="gold" icon={<Wallet />} label="Realized revenue" value={inr(m.realizedRevenue)}
              hint={Number(m.orderValue) > 0
                ? `${shareOf(m.realizedRevenue, m.orderValue)} of order value · ${num(dt.has.revenue)} day(s)`
                : 'nothing invoiced yet'}
              onClick={lens(dt.has.revenue, 'has', 'revenue')} active={f.has === 'revenue'} />
            {/* Same figure and same wording as the leaderboard's Visits done column and tile. */}
            <StatCard tone="sky" icon={<MapPin />} label="Visits done" value={num(m.visitsCompleted ?? m.visits)}
              hint={Number(m.retailerVisits) > 0
                ? `${pairOf(m.orders, m.retailerVisits)} orders per retailer visit`
                : 'no completed retailer visits'}
              onClick={lens(dt.has.visits, 'has', 'visits')} active={f.has === 'visits'} />
            <StatCard tone="brand" icon={<CalendarCheck />} label="On duty"
              value={pairOf(Number(m.daysPresent) + Number(m.halfDays), m.expectedDays)}
              hint={`${m.attendancePct}% attendance · ${num(m.absentDays)} absent`}
              onClick={lens(dt.onDuty, 'status', 'ON_DUTY')} active={f.status === 'ON_DUTY'} />
            <StatCard tone="neutral" icon={<UserPlus />} label="New customers"
              value={num(Number(m.newRetailers) + Number(m.newFarmers))}
              hint={`${num(m.newRetailers)} retailers · ${num(m.newFarmers)} farmers`}
              onClick={lens(dt.has.newCustomers, 'has', 'newCustomers')} active={f.has === 'newCustomers'} />
            {canSeeMoney && (
              <StatCard tone="sky" icon={<IndianRupee />} label="Claimed" value={inr(dt.claimed)}
                hint={`${inr(dt.paid)} already settled · ${num(dt.has.claims)} day(s)`}
                onClick={lens(dt.has.claims, 'has', 'claims')} active={f.has === 'claims'} />
            )}
            <StatCard
              tone={dt.flaggedHigh > 0 ? 'danger' : dt.flagged > 0 ? 'warning' : 'neutral'}
              icon={<TriangleAlert />} label="Flagged days" value={num(dt.flagged)}
              hint={dt.flaggedHigh > 0 ? `${num(dt.flaggedHigh)} high severity` : dt.flagged > 0 ? 'nothing severe' : 'no flags'}
              onClick={lens(dt.flagged, 'flag', 'ANY')} active={f.flag === 'ANY'} />
            {/*
              * Filters on DECLARED distance, not on `route` (days with a GPS trace).
              *
              * The tile shows self-declared km. Filtering by GPS trace meant that on a rep whose
              * handset never sent one — which this tile's own hint says is the case — clicking a card
              * reading "42 km" emptied the very table it was meant to explain.
              */}
            <StatCard tone="neutral" icon={<RouteIcon />} label="Distance declared" value={km(dt.declaredKm)}
              hint={dt.gpsKm > 0 ? `GPS supports ${km(dt.gpsKm)}` : 'no GPS trace to check against'}
              onClick={lens(dt.has.distance, 'has', 'distance')} active={f.has === 'distance'} />
          </div>

          {/* Attendance as one meter + the remaining supporting figures, grouped rather than
              scattered across a wall of equal-weight numbers. */}
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
            <div className="rounded-card border border-line bg-surface shadow-e1 p-4">
              <div className="mb-3 flex items-baseline justify-between gap-3">
                <span className="type-body-strong text-content">Attendance</span>
                <span className="type-body-strong tabular-nums text-content">{m.attendancePct}%</span>
              </div>
              {totalDays === 0 ? (
                <div className="rounded-control bg-surface-2 px-3 py-4 text-center text-xs text-subtle">No attendance recorded</div>
              ) : (
                <>
                  {/* 2px surface gaps between segments, per the mark spec. */}
                  <div className="flex h-2.5 w-full overflow-hidden rounded-pill bg-surface-3" role="img"
                    aria-label={`${present} present, ${half} half day, ${absent} absent of ${totalDays} days`}>
                    {seg.map((s, i) => (
                      <div key={s.key} style={{
                        width: `${(s.n / totalDays) * 100}%`, background: s.color,
                        marginLeft: i ? 2 : 0,
                      }} />
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
                    {[
                      { k: 'Present', n: present, c: seg.find((s) => s.key === 'Present')?.color ?? (isDark ? PALETTE.brand[500] : PALETTE.brand[600]) },
                      { k: 'Half day', n: half, c: isDark ? PALETTE.harvest[400] : PALETTE.harvest[500] },
                      { k: 'Absent', n: absent, c: isDark ? '#ff7875' : PALETTE.danger[500] },
                    ].map((s) => (
                      <span key={s.k} className="inline-flex items-center gap-1.5 text-xs">
                        <span className="size-2 shrink-0 rounded-full" style={{ background: s.c }} />
                        <span className="text-muted">{s.k}</span>
                        <span className="font-semibold tabular-nums text-content">{s.n}</span>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="rounded-card border border-line bg-surface shadow-e1 p-4">
              <div className="mb-3 type-body-strong text-content">Coverage &amp; growth</div>
              <div className="grid grid-cols-3 gap-3">
                <Fact label="Orders / visits" muted={m.ordersPerRetailerVisit == null}
                  value={m.ordersPerRetailerVisit == null ? '—' : pairOf(m.orders, m.retailerVisits)} />
                <Fact label="New retailers" value={num(m.newRetailers)} muted={!m.newRetailers} to={registrationsHref('RETAILER')} />
                <Fact label="New farmers" value={num(m.newFarmers)} muted={!m.newFarmers} to={registrationsHref('FARMER')} />
              </div>
            </div>
          </div>

          <DayGrid
            spId={spId} from={from} to={to}
            canSeeMoney={canSeeMoney} canSeeMap={canSeeMap} onOpenMap={setMapDate}
            filter={f} setFilter={setF} data={dayQuery.data} isFetching={dayQuery.isFetching}
            onReport={() => setRepExportOpen(true)}
          />

          {/* Rendered even when there is only one month in range. Hiding it entirely was a
              discoverability trap: someone looking for "month by month" found nothing and no hint
              that a wider period would produce it. */}
          {spansMonths
            ? <MonthGrid data={monthsQuery.data} isFetching={monthsQuery.isFetching} canSeeMoney={canSeeMoney} />
            : (
              <div className="mb-6 rounded-card border border-line bg-surface shadow-e1 p-4">
                <div className="mb-1 type-body-strong text-content">Month by month</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <Typography.Text type="secondary" className="text-xs">
                    This period is a single month, so there is nothing to compare it against.
                  </Typography.Text>
                  {onCompareMonths && (
                    <Button size="sm" variant="default" icon={<CalendarCheck size={15} />} onClick={onCompareMonths}>
                      Compare the last 12 months
                    </Button>
                  )}
                </div>
              </div>
            )}

          {/* Two measures on different scales — two charts, never one dual axis. */}
          <div className="grid gap-3 lg:grid-cols-2">
            <ChartCard title="Daily order value" total={inr(m.orderValue)} has={hasOrderValue} height={190}>
              <AreaChart data={daily} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="perfMoney" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.money} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={C.money} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                {/* Solid hairline, one shade off the surface — a dashed grid reads as a threshold. */}
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format('DD/MM')} fontSize={11}
                  stroke={C.axis} tickLine={false} axisLine={{ stroke: C.grid }} />
                <YAxis fontSize={11} width={52} stroke={C.axis} tickLine={false} axisLine={false}
                  tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))} />
                <RTooltip {...tipStyle} formatter={(v: any) => [inr(v), 'Order value']} labelFormatter={(d) => formatIstDate(String(d))} />
                <Area type="monotone" dataKey="value" stroke={C.money} strokeWidth={2} fill="url(#perfMoney)"
                  dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: token.colorBgContainer }} />
              </AreaChart>
            </ChartCard>

            <ChartCard title="Daily visits" total={`${num(m.visits)} total`} has={hasVisits} height={190}>
              <BarChart data={daily} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                {/* Solid hairline, one shade off the surface — a dashed grid reads as a threshold. */}
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format('DD/MM')} fontSize={11}
                  stroke={C.axis} tickLine={false} axisLine={{ stroke: C.grid }} />
                <YAxis allowDecimals={false} fontSize={11} width={32} stroke={C.axis} tickLine={false} axisLine={false} />
                <RTooltip {...tipStyle} formatter={(v: any) => [num(v), 'Visits']} labelFormatter={(d) => formatIstDate(String(d))} />
                {/* Capped so a single day's bar is a bar, not a slab across the plot. */}
                <Bar dataKey="visits" fill={C.visits} radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ChartCard>
          </div>
        </div>
      )}
      {mapDate && (
        <RouteMapModal spId={spId} name={data?.name ?? ''} from={mapDate} to={mapDate} onClose={() => setMapDate(null)} />
      )}
      <ExportDialog
        open={repExportOpen}
        onClose={() => setRepExportOpen(false)}
        // One person, this modal's own period. `spIds` narrows the requester's scope; it can never
        // widen it, so the server still refuses a rep this actor is not allowed to see.
        params={{ from, to, spIds: String(spId) }}
        scope={[]}
        order=""
        staffCount={1}
        only={{ id: String(spId), name: data?.name ?? 'this staff member' }}
      />
    </Modal>
  );
}

const DAY_TONE: Record<string, 'green' | 'gold' | 'blue' | 'red' | 'neutral'> = {
  PRESENT: 'green', HALF_DAY: 'gold', LEAVE: 'blue', HOLIDAY: 'blue', WEEKLY_OFF: 'neutral', ABSENT: 'red',
};
/** PRESENT → "Present", HALF_DAY → "Half day" — for filter labels, which are prose, not enums. */
const prettyStatus = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

/**
 * Day-by-day detail for one rep: what they did, what it cost, what looks wrong, and a way straight
 * into that day's movement map. This is the grain the anomalies actually live at — the leaderboard
 * only counts them.
 */
function DayGrid({ spId, from, to, canSeeMoney, canSeeMap, onOpenMap, filter, setFilter, data, isFetching, onReport }: {
  spId: string; from: string; to: string; canSeeMoney: boolean; canSeeMap: boolean; onOpenMap: (d: string) => void;
  filter: DayFilter; setFilter: (f: DayFilter) => void; data: any; isFetching: boolean;
  /** Opens the full report dialog (Excel / PDF, own period, preview) for this rep. */
  onReport?: () => void;
}) {
  const { token } = theme.useToken();
  const [exporting, setExporting] = useState(false);

  // Rows arrive already filtered — the server owns every filter, so the grid, the counts above it and
  // the exported file are three views of one query rather than three implementations of one idea.
  const rows: any[] = data?.rows ?? [];
  const t = { ...EMPTY_DAY_TOTALS, ...(data?.totals ?? {}) };
  const dayCount = Number(data?.dayCount ?? rows.length);
  const matched = Number(data?.matched ?? rows.length);
  const anyDayFilter = !!(filter.status || filter.has || filter.flag);
  const set = (k: keyof DayFilter, v: any) => setFilter({ ...filter, [k]: v || undefined });

  /* Options come from the server's counts, so a filter that would return nothing says so BEFORE it
     is picked, and no option is ever offered for data this period does not contain. */
  // Every option carries the server's count AND is DISABLED at zero. Showing "(0)" was not enough —
  // people picked it anyway and got an empty grid, which reads as a broken screen rather than as an
  // honest answer. An option whose only possible outcome is "nothing" should not be selectable.
  const statusOptions = [
    { label: `On duty (${num(t.onDuty)})`, value: 'ON_DUTY', disabled: !t.onDuty },
    ...Object.entries(t.statusCounts).map(([k, n]) => ({ label: `${prettyStatus(k)} (${num(n)})`, value: k, disabled: !n })),
  ];
  const HAS_LABEL: Array<[string, string]> = [
    ['orders', 'Has an order'],
    ['revenue', 'Has realized revenue'],
    ['visits', 'Has a visit'],
    ['newCustomers', 'Has a new customer'],
    ...(canSeeMoney ? ([['claims', 'Has an expense claim']] as Array<[string, string]>) : []),
    ['distance', 'Has declared distance'],
    ['route', 'Has a GPS trace'],
    ['noWork', 'On duty, nothing to show'],
  ];
  const hasOptions = HAS_LABEL.map(([v, l]) => ({
    label: `${l} (${num(t.has[v] ?? 0)})`, value: v, disabled: !(t.has[v] ?? 0),
  }));
  const countedFlags = Object.entries(t.flagCounts).filter(([k]) => isCounted(k));
  const noteFlags = Object.entries(t.flagCounts).filter(([k]) => !isCounted(k));
  const flagOptions: any[] = [
    { label: `Any flag (${num(t.flagged)})`, value: 'ANY', disabled: !t.flagged },
    { label: `High severity (${num(t.flaggedHigh)})`, value: 'HIGH', disabled: !t.flaggedHigh },
    ...(countedFlags.length
      ? [{ label: 'Flags', options: countedFlags.map(([k, n]) => ({ label: `${flagMeta(k).label} (${num(n)})`, value: k })) }]
      : []),
    // Low-severity flags are evidence notes and are never counted as anomalies — but they are still
    // worth filtering to when the question is what the GPS did or did not record.
    ...(noteFlags.length
      ? [{ label: 'Evidence notes', options: noteFlags.map(([k, n]) => ({ label: `${flagMeta(k).label} (${num(n)})`, value: k })) }]
      : []),
  ];

  // Plain-language names for whatever is applied, for the empty-state message.
  const appliedLabels = [
    filter.status ? (filter.status === 'ON_DUTY' ? 'on duty' : prettyStatus(filter.status).toLowerCase()) : null,
    filter.has ? (HAS_LABEL.find(([v]) => v === filter.has)?.[1] ?? filter.has).toLowerCase() : null,
    filter.flag === 'ANY' ? 'flagged' : filter.flag === 'HIGH' ? 'flagged high severity' : filter.flag ? flagMeta(filter.flag).label.toLowerCase() : null,
  ].filter(Boolean) as string[];

  // Export what is on screen: the same three filters ride along, and the server re-applies them.
  const exportDays = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ from, to });
      if (filter.status) qs.set('status', filter.status);
      if (filter.has) qs.set('has', filter.has);
      if (filter.flag) qs.set('flag', filter.flag);
      await downloadCsv(
        `/performance/sales/${spId}/days/export?${qs.toString()}`,
        `performance-days${anyDayFilter ? '-filtered' : ''}_${from}_to_${to}.csv`,
      );
    } catch {
      message.error('Could not export. Try a narrower period.');
    } finally {
      setExporting(false);
    }
  };

  const columns: any[] = [
    { title: 'Date', dataIndex: 'date', width: 132, fixed: 'left',
      render: (v: string) => <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{dayjs(v).format('ddd, DD MMM')}</span> },
    // An ON_BEHALF day was typed in by an admin — no device, no GPS, no geofence — so "no work
    // recorded" is expected rather than suspicious. Flagging it identically to a self-punched day
    // is what made test-generated attendance read as fraud, so the origin is shown next to the day.
    { title: 'Day', dataIndex: 'status', width: 150,
      render: (v: string, r: any) => {
        if (!v) return <span style={{ color: token.colorTextTertiary }}>—</span>;
        return (
          <Space size={4} wrap>
            <Tag tone={DAY_TONE[v] ?? 'neutral'}>{v.replace(/_/g, ' ').toLowerCase()}</Tag>
            {r.source === 'ON_BEHALF' && (
              <Tooltip title={`Marked by an admin, not punched by the rep${r.onBehalfReason ? ` — "${r.onBehalfReason}"` : ''}. No device GPS or geofence for this day.`}>
                <Tag tone="purple">on behalf</Tag>
              </Tooltip>
            )}
          </Space>
        );
      } },
    // Wide enough for "08:55 AM → 03:30 PM" on ONE line — wrapping doubled every row's height and
    // halved how much of the period you could see at once.
    { title: 'In / Out', key: 'io', width: 152, render: (_: any, r: any) => {
      const cell = (
        <span style={{ color: token.colorTextSecondary, whiteSpace: 'nowrap', borderBottom: r.shifts > 1 ? '1px dotted currentColor' : undefined }} className="text-xs">
          {r.checkIn ? formatIstTime(r.checkIn) : '—'} → {r.checkOut ? formatIstTime(r.checkOut) : '—'}
        </span>
      );
      // Several shifts: the pair is the first punch-in and the last punch-out, and the hours worked are
      // the shifts added up — a flag like SHORT_DAY reads off that sum, not off this span.
      return r.shifts > 1 ? (
        <Tooltip title={`First punch-in to last punch-out, across ${r.shifts} shifts. ${r.workedMin != null ? `${Math.floor(r.workedMin / 60)}h ${r.workedMin % 60}m worked` : 'No shift closed yet'} — the gaps between shifts are not counted.`}>
          {cell}
        </Tooltip>
      ) : cell;
    } },
    { title: 'Visits', dataIndex: 'visits', align: 'right', width: 92,
      render: (v: number, r: any) => {
        const open = Math.max(0, v - (r.visitsCompleted ?? v));
        const parts = [`${num(v)} reached`];
        if (open > 0) parts.push(`${num(open)} still open`);
        if (r.visitsCancelled > 0) parts.push(`${num(r.visitsCancelled)} set off for but never reached`);
        return (
          <Tooltip title={parts.join(' · ')}>
            <span style={{ color: v > 0 ? undefined : token.colorTextTertiary }}>
              {num(v)}
              {r.visitsCancelled > 0 && (
                <span style={{ color: token.colorTextTertiary, marginInlineStart: 4 }}>+{num(r.visitsCancelled)}</span>
              )}
            </span>
          </Tooltip>
        );
      } },
    { title: 'Orders', dataIndex: 'orders', align: 'right', width: 92, render: (v: number) => num(v) },
    { title: 'New cust.', key: 'nc', align: 'right', width: 104,
      render: (_: any, r: any) => (
        <Tooltip title={`${num(r.newRetailers)} retailer(s), ${num(r.newFarmers)} farmer(s)`}>
          <span>{num((r.newRetailers ?? 0) + (r.newFarmers ?? 0))}</span>
        </Tooltip>
      ) },
    { title: 'Distance', key: 'km', align: 'right', width: 118, render: (_: any, r: any) => (
      <Tooltip title={r.gpsPings > 0 ? `${km(r.gpsKm)} from ${num(r.gpsPings)} GPS pings` : 'No GPS trace for this day'}>
        <span style={{ color: r.declaredKm > 0 ? undefined : token.colorTextTertiary }}>{r.declaredKm > 0 ? km(r.declaredKm) : '—'}</span>
      </Tooltip>
    ) },
    // The amount LINKS to the claim it came from — spotting a bad day is only useful if you can act
    // on it, and hunting for the claim by rep and date was the missing step.
    ...(canSeeMoney ? [{
      title: 'Claimed', dataIndex: 'claimed', align: 'right' as const, width: 120,
      render: (v: number, r: any) => {
        if (!(v > 0)) return <span style={{ color: token.colorTextTertiary }}>—</span>;
        const claims: Array<{ id: string; reportNo: string }> = r.claims ?? [];
        const status = r.claimStatuses?.length ? r.claimStatuses.join(', ').toLowerCase().replace(/_/g, ' ') : '';
        const label = (
          <span style={{ color: r.paid > 0 ? token.colorError : undefined, fontWeight: r.paid > 0 ? 600 : 400 }}>{inr(v)}</span>
        );
        if (!claims.length) return <Tooltip title={status || undefined}>{label}</Tooltip>;
        // `claims` is a capped prefix — say how many more there are rather than imply it's the lot.
        const more = Math.max(0, (r.claimCount ?? claims.length) - claims.length);
        const names = claims.map((c) => c.reportNo).join(', ') + (more > 0 ? ` +${more} more` : '');
        return (
          <Tooltip title={`${names}${status ? ` · ${status}` : ''} — open the claim`}>
            <a
              className="cursor-pointer"
              onClick={(e: any) => { e.stopPropagation(); window.open(`/expense-reports/${claims[0].id}`, '_blank', 'noopener'); }}
            >
              {label}
            </a>
          </Tooltip>
        );
      },
    }] : []),
    { title: 'Flags', dataIndex: 'flags', width: 250,
      render: (v: string[], r: any) => <FlagList flags={v ?? []} row={r} muted={token.colorTextTertiary} /> },
    // Same affordance as the Attendance log's Route column — same icon, label and styling, so the
    // two places that open a day's movement look like the same thing. Hidden on a day with nothing
    // to plot, rather than opening an empty map.
    ...(canSeeMap ? [{
      title: 'Route', key: 'route', width: 96,
      render: (_: any, r: any) => (r.gpsPings > 0 || r.visits > 0 || r.checkIn ? (
        <a
          className="inline-flex cursor-pointer items-center gap-1.5 text-brand-700 hover:text-brand-800 dark:text-brand-400"
          onClick={(e: any) => { e.stopPropagation(); onOpenMap(r.date); }}
        >
          <RouteIcon size={16} className="shrink-0" />
          Route
        </a>
      ) : <span style={{ color: token.colorTextTertiary }}>—</span>),
    }] : []),
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Typography.Text strong>Day by day</Typography.Text>
        {/* The shape of the period up front, so a long one doesn't have to be scrolled to be understood. */}
        <Typography.Text type="secondary" className="text-xs">
          {anyDayFilter
            ? `${matched} of ${dayCount} day${dayCount === 1 ? '' : 's'}`
            : `${dayCount} day${dayCount === 1 ? '' : 's'} in this period`}
          {t.flagged > 0 && <> · <span style={{ color: token.colorWarning, fontWeight: 600 }}>{t.flagged} flagged</span></>}
          {/* Name what is narrowing the list. "1 of 2 days" beside a tile reading 2 is only honest if
              the reader can see WHICH filter took the other day away. */}
          {anyDayFilter && <> · showing <span className="text-content">{appliedLabels.join(' · ')}</span></>}
        </Typography.Text>
      </div>
      {/* One filter per question a reviewer actually asks — which days was he on duty, what happened
          on them, and what looks wrong — each resolved by the server. */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Select
          size="sm" allowClear placeholder="Any day"
          value={filter.status ?? null} onChange={(v: any) => set('status', v)}
          options={statusOptions} style={{ width: 168 }}
        />
        <Select
          size="sm" allowClear placeholder="Any activity"
          value={filter.has ?? null} onChange={(v: any) => set('has', v)}
          options={hasOptions} style={{ width: 214 }}
        />
        <Select
          size="sm" allowClear placeholder="Any flag"
          value={filter.flag ?? null} onChange={(v: any) => set('flag', v)}
          options={flagOptions} style={{ width: 204 }}
        />
        {anyDayFilter && (
          <FilterResetButton onClick={() => setFilter({})} />
        )}
        {/* Two different things, so two buttons rather than one ambiguous "Export": the CSV is
            exactly the filtered rows on screen; the report is the formatted Excel/PDF document. */}
        <Button
          className="ml-auto" size="sm" icon={<Download size={16} />}
          loading={exporting} disabled={!rows.length} onClick={exportDays}
        >
          CSV of these days
        </Button>
        {onReport && (
          <Button size="sm" variant="default" icon={<FileText size={16} />} onClick={onReport}>
            Excel / PDF report…
          </Button>
        )}
      </div>
      <Table
        // Remounting on a filter change drops the pager back to page 1 — otherwise narrowing to a
        // short list while on page 4 shows an empty table.
        key={`${filter.status ?? ''}|${filter.has ?? ''}|${filter.flag ?? ''}`}
        rowKey="date"
        size="small"
        loading={isFetching}
        dataSource={rows}
        columns={columns}
        scroll={{ x: canSeeMoney ? 1232 : 1124 }}
        // A year is 250+ working days. Paging keeps the drawer a predictable height and keeps the
        // charts below it reachable, without hiding anything the way a silent cap would.
        pagination={{ pageSize: 8, showSizeChanger: false, size: 'small', showTotal: (t) => `${t} day${t === 1 ? '' : 's'}` }}
        locale={{ emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={anyDayFilter ? (
              // Each filter is individually non-empty (zero-count options are disabled), so an empty
              // grid here means the COMBINATION excludes everything. Say which combination, or the
              // reader is left guessing which of three controls did it.
              <span>
                No day matches all of{' '}
                <span className="font-medium text-content">{appliedLabels.join(' · ')}</span>
                {' — '}
                <button type="button" className="text-brand-700 underline hover:text-brand-800 dark:text-brand-400" onClick={() => setFilter({})}>
                  clear the filters
                </button>
              </span>
            ) : 'No activity in this period'}
          />
        ) }}
      />
    </div>
  );
}

/**
 * One rep, a row per calendar month — the trend the single-period board cannot show.
 *
 * Every figure is summed from the SAME day facts the day grid above is built from, so a month and the
 * days inside it can never disagree. A month clipped by the chosen range (or still running) is marked
 * "part month", because a low figure there means "not finished", not "did badly".
 */
function MonthGrid({ data, isFetching, canSeeMoney }: { data: any; isFetching: boolean; canSeeMoney: boolean }) {
  const { token } = theme.useToken();
  const rows: any[] = data?.months ?? [];

  const columns: any[] = [
    { title: 'Month', dataIndex: 'month', width: 130, fixed: 'left',
      render: (v: string, r: any) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontWeight: 600 }}>{dayjs(`${v}-01`).format('MMM YYYY')}</span>
          {r.partial && (
            <Tooltip title={`Only ${formatIstDate(r.from)} → ${formatIstDate(r.to)} of this month is in the selected period, so these figures are not a full month.`}>
              <span style={{ color: token.colorTextTertiary, marginInlineStart: 6, fontSize: 11 }}>part</span>
            </Tooltip>
          )}
        </span>
      ) },
    { title: 'Orders', dataIndex: 'orders', align: 'right', width: 80,
      render: (v: number, r: any) => (r.ordersRejected > 0 ? (
        <Tooltip title={`${num(v)} counted · ${num(r.ordersRejected)} rejected at approval and excluded`}>
          <span>{num(v)}</span>
        </Tooltip>
      ) : num(v)) },
    { title: 'Order value', dataIndex: 'orderValue', align: 'right', width: 120, render: (v: number) => inr(v) },
    { title: 'Realized', dataIndex: 'realizedRevenue', align: 'right', width: 110,
      render: (v: number) => <b style={{ color: PALETTE.brand[600] }}>{inr(v)}</b> },
    { title: 'Visits', dataIndex: 'visits', align: 'right', width: 80, render: (v: number) => num(v) },
    { title: 'New cust.', key: 'nc', align: 'right', width: 95,
      render: (_: any, r: any) => (
        <Tooltip title={`${num(r.newRetailers)} retailer(s), ${num(r.newFarmers)} farmer(s)`}>
          <span>{num(r.newRetailers + r.newFarmers)}</span>
        </Tooltip>
      ) },
    { title: 'Present', key: 'pres', align: 'right', width: 100,
      render: (_: any, r: any) => (
        <Tooltip title={`${num(r.daysPresent)} present · ${num(r.halfDays)} half day(s) · ${num(r.absentDays)} absent of ${num(r.expectedDays)} working day(s)`}>
          <span>{pairOf(r.daysPresent, r.expectedDays)}</span>
        </Tooltip>
      ) },
    { title: 'Attend.', dataIndex: 'attendancePct', align: 'right', width: 90,
      render: (v: number) => <Tag color={v >= 90 ? 'green' : v >= 70 ? 'gold' : 'red'}>{v}%</Tag> },
    { title: 'Distance', dataIndex: 'declaredKm', align: 'right', width: 100,
      render: (v: number) => (v > 0 ? km(v) : <span style={{ color: token.colorTextTertiary }}>—</span>) },
    ...(canSeeMoney ? [{
      title: 'Claimed', dataIndex: 'claimed', align: 'right' as const, width: 110,
      render: (v: number, r: any) => (
        <Tooltip title={`${inr(r.paid)} already settled`}>
          <span style={{ color: v > 0 ? undefined : token.colorTextTertiary }}>{v > 0 ? inr(v) : '—'}</span>
        </Tooltip>
      ),
    }] : []),
    { title: 'Flagged', dataIndex: 'flaggedDays', align: 'center', width: 100,
      render: (v: number, r: any) => (v > 0
        ? (
          <Tooltip title={r.noWorkClaimedDays > 0
            ? `${num(r.noWorkClaimedDays)} of these had expenses claimed against a day with no work`
            : 'Days carrying a work-integrity flag'}>
            <Tag tone={r.noWorkClaimedDays > 0 ? 'red' : 'gold'}>{num(v)} day{v === 1 ? '' : 's'}</Tag>
          </Tooltip>
        )
        : <span style={{ color: token.colorTextTertiary }}>—</span>) },
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3">
        <Typography.Text strong>Month by month</Typography.Text>
        <Typography.Text type="secondary" className="text-xs">
          {rows.length} month{rows.length === 1 ? '' : 's'} in this period · newest first
        </Typography.Text>
      </div>
      <Table
        rowKey="month"
        size="small"
        loading={isFetching}
        dataSource={rows}
        columns={columns}
        scroll={{ x: canSeeMoney ? 1115 : 1005 }}
        pagination={false}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No months in this period" /> }}
      />
    </div>
  );
}

/** Up to two flags inline, the rest folded into a "+N" chip — a day can carry five and the row
 *  still has to stay readable. */
function FlagList({ flags, row, muted }: { flags: string[]; row?: any; muted: string }) {
  if (!flags.length) return <span style={{ color: muted }}>—</span>;
  const ordered = [...flags].sort((a, b) => Number(isCounted(b)) - Number(isCounted(a)));
  const shown = ordered.slice(0, 2);
  const rest = ordered.slice(2);
  // Give the speed flag its actual numbers — "impossible" is only credible if you can see why.
  const hintOf = (f: string) => {
    const base = flagMeta(f).hint;
    if (f === 'IMPOSSIBLE_TRAVEL' && row?.maxKmh) {
      return `${base} Fastest leg: ${km(row.maxGapKm)} at about ${num(row.maxKmh)} km/h.`;
    }
    return base;
  };
  return (
    <Space size={4} wrap>
      {shown.map((f) => {
        const meta = flagMeta(f);
        return <Tooltip key={f} title={hintOf(f)}><Tag tone={meta.tone}>{meta.label}</Tag></Tooltip>;
      })}
      {rest.length > 0 && (
        <Tooltip title={rest.map((f) => flagMeta(f).label).join(', ')}>
          <Tag tone="neutral">+{rest.length}</Tag>
        </Tooltip>
      )}
    </Space>
  );
}

